import React, { useEffect, useRef, useState } from "react";
import { getCurrencySymbol } from '../utils/currency';
import { getAppISD, COUNTRIES } from '../pages/Settings';
const stripAnyISD = (phone) => {
  const sorted = [...COUNTRIES].sort((a, b) => b.isd.length - a.isd.length);
  for (const c of sorted) { if ((phone || '').startsWith(c.isd)) return phone.slice(c.isd.length); }
  return phone || '';
};
import { useToast } from "../contexts/ToastContext";
import {
  FaTimes,
  FaArrowLeft,
  FaArrowRight,
  FaTrash,
  FaEdit,
  FaChevronDown,
  FaChevronUp,
  FaStar,
} from "react-icons/fa";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  writeUserDocument,
  readUserCollection,
  moveUserDocument,
} from "@services/firebaseUser";
import { getFirebase } from "@services/firebase";
import {
  readFlatTemplate,
  fetchCombinations,
  readGlobalProfiles,
} from "@services/firebaseGlobal";
import "./css/EnquiryLargeCard.css";

const _ELC_LABEL_TO_SUFFIX = {
  "1": "10",
  "1.5": "15",
  "2": "20",
  "2.5": "25",
  "3": "30",
  "3.5": "35",
  "4": "40",
  "4.5": "45",
  "5": "50",
  "5.5": "55",
  "6": "60",
};

const _getBackendUrl = () => {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  return isLocal
    ? (import.meta.env.VITE_BACKEND_URL_LOCAL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3002')
    : (import.meta.env.VITE_BACKEND_URL || window.location.origin);
};

let _db;
const getDb = () => {
  if (!_db) {
    const { db } = getFirebase();
    _db = db;
  }
  return _db;
};

const EnquiryLargeCard = ({
  profile,
  user,
  userRole,
  enquiryId,
  collectionName: initialCollection = "enquiry",
  onClose,
  onProfileMoved,
}) => {
  const showToast = useToast();
  const [isdCode, setIsdCode] = useState(() => getAppISD());
  const isSuper = userRole === "Super";
  const isAdmin = userRole === "Admin";
  const isNormalUser = userRole === "Buyer";
  const rolePrefixes = [userRole.toLowerCase()];

  // Image carousel
  const [images, setImages] = useState([]);
  const [imgIndex, setImgIndex] = useState(0);
  const [imgDeleted, setImgDeleted] = useState(false);
  const [imageCosts, setImageCosts] = useState([]);
  const [initialImageCosts, setInitialImageCosts] = useState([]);

  // Cake form state
  const [formData, setFormData] = useState({
    cover: "",
    frosting: "",
    eggless: false,
    glutenFree: false,
    tiers: [],
    quantity: "",
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [costest, setCostest] = useState(null);
  const [initialCostest, setInitialCostest] = useState(null);
  const [cakeChargeMap, setCakeChargeMap] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const hasChangesRef = useRef(false);
  const saveTimerRef = useRef(null);
  const handleSaveRef = useRef(null);
  const profileInitEnquiryRef = useRef(null); // tracks which enquiryId was last initialised

  // Collapsible toggles
  const [specOpen, setSpecOpen] = useState(false);
  const [delivOpen, setDelivOpen] = useState(false);
  const [addSpecOpen, setAddSpecOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(true);

  // Combination options
  const [combinations, setCombinations] = useState([]);
  const [coverCombinations, setCoverCombinations] = useState([]);
  const [coverOptions, setCoverOptions] = useState([]);
  const [flavourOptions, setFlavourOptions] = useState([]);

  // Req state / editability
  const [reqState, setReqState] = useState("");
  const [currentCollection, setCurrentCollection] = useState(initialCollection);
  const [creatorUserId, setCreatorUserId] = useState("");
  const [processControls, setProcessControls] = useState([]);
  const [otimelineData, setOtimelineData] = useState([]);
  const [exceptionTimelines, setExceptionTimelines] = useState([]);
  const [orderReceivedIdNumber, setOrderReceivedIdNumber] = useState(null);
  const [isOtimelineLoaded, setIsOtimelineLoaded] = useState(false);
  const [isProcessControlsLoaded, setIsProcessControlsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isCakeReadOnly, setIsCakeReadOnly] = useState(true);

  // Delivery / address state
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [initialAddress, setInitialAddress] = useState("");
  const [newAddress, setNewAddress] = useState({
    streetName: "",
    houseNumber: "",
    pinCode: "",
    pincodeRaw: "",
    cityRaw: "",
  });
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [addAsHomeAddress, setAddAsHomeAddress] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState("Delivery");
  const [initialDeliveryMode, setInitialDeliveryMode] = useState("Delivery");
  const [addressSet, setAddressSet] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [initialDeliveryDate, setInitialDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [initialDeliveryTime, setInitialDeliveryTime] = useState("");
  const [reviewFeedback, setReviewFeedback] = useState("");
  const [initialReviewFeedback, setInitialReviewFeedback] = useState("");

  // Phone / contact state
  const [profilePhone, setProfilePhone] = useState("");
  const [profileDocId, setProfileDocId] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [showAddPhoneForm, setShowAddPhoneForm] = useState(false);
  const [addAsPrimaryPhone, setAddAsPrimaryPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSendingCode, setPhoneSendingCode] = useState(false);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneResendCountdown, setPhoneResendCountdown] = useState(0);
  const [phoneCodeInput, setPhoneCodeInput] = useState("");
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  // Address autocomplete
  const [elcLocSuggestions, setElcLocSuggestions] = useState([]);
  const [elcLocLoading, setElcLocLoading] = useState(false);
  const [elcStreetSuggestions, setElcStreetSuggestions] = useState([]);
  const [elcStreetLoading, setElcStreetLoading] = useState(false);
  const elcLocDebounceRef = useRef(null);
  const elcStreetDebounceRef = useRef(null);
  const elcAllLocalitiesRef = useRef(null);
  const elcLocalitiesLoadingRef = useRef(false);
  const elcStreetCacheRef = useRef({});

  // Delivery charge auto-calculation
  const [deliveryCharges, setDeliveryCharges] = useState([]);
  const [delCostLoading, setDelCostLoading] = useState(false);
  const [autoCalcKm, setAutoCalcKm] = useState(null); // km shown next to field
  const elcAdminAddressRef = useRef(null);
  const elcDistCacheRef = useRef({});
  const lastAutoCalcAddrRef = useRef("");

  // Extra fields
  const [dispMsg, setDispMsg] = useState("");
  const [initialDispMsg, setInitialDispMsg] = useState("");
  const [msgPos, setMsgPos] = useState("");
  const [initialMsgPos, setInitialMsgPos] = useState("");
  const [nameCake, setNameCake] = useState("");
  const [initialNameCake, setInitialNameCake] = useState("");
  const [delCost, setDelCost] = useState("");
  const [initialDelCost, setInitialDelCost] = useState("");
  const [specialWishes, setSpecialWishes] = useState("");
  const [initialSpecialWishes, setInitialSpecialWishes] = useState("");

  // Calendar / time window
  const [holidayCalendar, setHolidayCalendar] = useState([]);
  const [timeWindows, setTimeWindows] = useState([]);
  const [isHolidayCalendarLoaded, setIsHolidayCalendarLoaded] = useState(false);
  const [offDayWarning, setOffDayWarning] = useState(false);

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showChangeButtons, setShowChangeButtons] = useState(false);
  const [changeAction, setChangeAction] = useState(null);
  const [showOptionDropdown, setShowOptionDropdown] = useState(false);
  const [optionAction, setOptionAction] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [showAcceptConfirmModal, setShowAcceptConfirmModal] = useState(false);
  const [acceptCheck1, setAcceptCheck1] = useState(false);
  const [acceptCheck2, setAcceptCheck2] = useState(false);
  const [acceptCheck3, setAcceptCheck3] = useState(false);
  const [showCommentRequiredPopup, setShowCommentRequiredPopup] =
    useState(false);
  const [requiredCommentText, setRequiredCommentText] = useState("");
  const [pendingStateLabel, setPendingStateLabel] = useState("");
  const pendingStateChangeFnRef = useRef(null);
  const pendingFeedRef = useRef(null);
  // Refs to hold latest delivery values without triggering subscription restarts
  const deliveryTimeRef = useRef(deliveryTime);
  const deliveryDateRef = useRef(deliveryDate);
  const deliveryModeRef = useRef(deliveryMode);
  const selectedAddressRef = useRef(selectedAddress);
  useEffect(() => {
    const onCountryChange = () => setIsdCode(getAppISD());
    window.addEventListener('appCountryChanged', onCountryChange);
    window.addEventListener('storage', onCountryChange);
    return () => {
      window.removeEventListener('appCountryChanged', onCountryChange);
      window.removeEventListener('storage', onCountryChange);
    };
  }, []);
  useEffect(() => {
    deliveryTimeRef.current = deliveryTime;
  }, [deliveryTime]);
  useEffect(() => {
    deliveryDateRef.current = deliveryDate;
  }, [deliveryDate]);
  useEffect(() => {
    deliveryModeRef.current = deliveryMode;
  }, [deliveryMode]);
  useEffect(() => {
    selectedAddressRef.current = selectedAddress;
  }, [selectedAddress]);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");

  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const writeQueueRef = useRef(Promise.resolve());
  const [showCostMissingPopup, setShowCostMissingPopup] = useState(false);
  const [showOngoingEnquiryModal, setShowOngoingEnquiryModal] = useState(false);
  const [showDelCostMissingPopup, setShowDelCostMissingPopup] = useState(false);
  const [showImageCheckPopup, setShowImageCheckPopup] = useState(false);

  // ── Min date: +2 days for non-Super/Admin ──────────────────────────────
  const minDeliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + (isSuper || isAdmin ? 0 : 2));
    return d.toISOString().split("T")[0];
  })();

  // ── Initialise form from profile prop ─────────────────────────────────
  useEffect(() => {
    if (!profile) return;
    // Guard: only fully reinitialise when a *different* enquiry is opened.
    // A parent re-render with a new profile object (e.g. triggered by onSnapshot)
    // must not clobber the user's in-progress edits (glutenFree, costest, etc.).
    const isNewEnquiry = profileInitEnquiryRef.current !== enquiryId;
    profileInitEnquiryRef.current = enquiryId;
    if (!isNewEnquiry) return;

    const imgs =
      profile.images && profile.images.length > 0 ? profile.images : [];
    setImages(imgs);
    setImgIndex(0);
    setImgDeleted(false);

    const costs = imgs.map((_, i) => {
      const key = i === 0 ? "ccost" : `ccost${i + 1}`;
      return profile[key] ? String(profile[key]).replace(/^€|^EUR/i, "") : "";
    });
    setImageCosts(costs);
    setInitialImageCosts([...costs]);

    const tierCount = profile.tierCount || 1;
    let normalizedTiers;
    if (profile.tiers && profile.tiers.length > 0) {
      normalizedTiers = profile.tiers.slice(0, tierCount).map((t, i) => ({
        tier: i + 1,
        cflavour: t.cflavour || "",
        cfilling: t.cfilling || "",
        sponge: t.sponge || "",
      }));
    } else {
      normalizedTiers = [
        {
          tier: 1,
          cflavour: profile.cflavour || "",
          cfilling: profile.cfilling || "",
          sponge: "",
        },
      ];
    }

    const fd = {
      cover: profile.cover || "",
      frosting: profile.frosting || "",
      eggless: profile.eggless || false,
      glutenFree: profile.glutenFree || false,
      tiers: normalizedTiers,
      quantity: profile.quantity != null ? String(profile.quantity).replace(/\s*kg$/i, '').trim() : "",
    };
    setFormData(fd);
    setInitialFormData(JSON.parse(JSON.stringify(fd)));

    const dc = profile.del_cost != null ? String(profile.del_cost).replace(/[^0-9.]/g, "") : "";
    setDelCost(dc);
    setInitialDelCost(dc);

    const ct = profile.costest ? String(profile.costest).replace(/[^0-9.]/g, "") || null : null;
    setCostest(ct);
    setInitialCostest(ct);
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch combinations + timeline + processControls + calendar ─────────
  useEffect(() => {
    const load = async () => {
      try {
        const [combi, coverCombi, ot, pc, hc, tw, et, dc, chargeItems] =
          await Promise.all([
            fetchCombinations("oCombination"),
            fetchCombinations("oCoverCombi"),
            readFlatTemplate("otimeline"),
            fetchCombinations("oprscontrol"),
            readFlatTemplate("dHolidayCalendar"),
            readFlatTemplate("dTimewindow"),
            fetchCombinations("oexptimeline"),
            fetchCombinations("dDeliveryCharge"),
            fetchCombinations("cCakeCharge"),
          ]);
        const chargeMap = {};
        chargeItems.forEach(({ variant, cost }) => {
          if (variant) chargeMap[variant] = cost;
        });
        setCakeChargeMap(chargeMap);
        setCombinations(combi);
        setCoverCombinations(coverCombi);
        setCoverOptions(
          [...new Set(coverCombi.map((c) => c.cover).filter(Boolean))].sort(
            (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        );
        setFlavourOptions(
          [...new Set(combi.map((c) => c.flavour).filter(Boolean))].sort(
            (a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })
          )
        );
        setOtimelineData(ot);
        const orEntry = ot.find((e) => e.name === "Order Received");
        if (orEntry)
          setOrderReceivedIdNumber(parseInt(orEntry.id.replace("id", ""), 10));
        setIsOtimelineLoaded(true);
        setProcessControls(pc);
        setIsProcessControlsLoaded(true);
        setHolidayCalendar(hc);
        setIsHolidayCalendarLoaded(true);
        setTimeWindows(tw);
        setExceptionTimelines(et);
        setDeliveryCharges(dc);
      } catch (err) {
        console.error("EnquiryLargeCard: error loading supporting data", err);
      }
    };
    load();
  }, []);

  // Normalize formData casing once combinations are loaded (Home cards store
  // values in lowercase e.g. "butter cream" but option lists use proper casing).
  useEffect(() => {
    if (!combinations.length || !coverCombinations.length) return;
    const normalize = (prev) => {
      if (!prev) return prev;
      const nCover =
        coverOptions.find(
          (o) => o.toLowerCase() === (prev.cover || "").toLowerCase()
        ) ||
        prev.cover ||
        "";
      const nFrosting =
        prev.frosting ||
        coverCombinations.find((c) => c.cover === nCover)?.frosting ||
        "";
      const nTiers = prev.tiers.map((t) => {
        const nFlavour =
          flavourOptions.find(
            (o) => o.toLowerCase() === (t.cflavour || "").toLowerCase()
          ) ||
          t.cflavour ||
          "";
        const fillings = [
          ...new Set(
            combinations
              .filter((c) => c.flavour === nFlavour)
              .map((c) => c.filling)
              .filter(Boolean)
          ),
        ];
        const nFilling =
          fillings.find(
            (o) => o.toLowerCase() === (t.cfilling || "").toLowerCase()
          ) ||
          t.cfilling ||
          "";
        const sponges = [
          ...new Set(
            combinations
              .filter((c) => c.flavour === nFlavour)
              .flatMap((c) => (Array.isArray(c.sponge) ? c.sponge : [c.sponge]))
              .filter(Boolean)
          ),
        ];
        const nSponge =
          sponges.find(
            (o) => o.toLowerCase() === (t.sponge || "").toLowerCase()
          ) ||
          t.sponge ||
          "";
        return {
          ...t,
          cflavour: nFlavour,
          cfilling: nFilling,
          sponge: nSponge,
        };
      });
      if (
        nCover === prev.cover &&
        nFrosting === prev.frosting &&
        JSON.stringify(nTiers) === JSON.stringify(prev.tiers)
      )
        return prev;
      return { ...prev, cover: nCover, frosting: nFrosting, tiers: nTiers };
    };
    setFormData((prev) => normalize(prev));
    setInitialFormData((prev) => normalize(prev));
  }, [combinations, coverCombinations, coverOptions, flavourOptions]);

  // ── Real-time listener for reqState ───────────────────────────────────
  useEffect(() => {
    const db = getDb();
    if (
      !db ||
      !enquiryId ||
      !otimelineData.length ||
      orderReceivedIdNumber === null
    )
      return;
    const docRef = doc(db, currentCollection, enquiryId);
    const unsub = onSnapshot(docRef, async (snap) => {
      if (!snap.exists()) {
        // Doc may have been moved to oldOrder by admin/mover € reattach there
        if (currentCollection === "order") {
          const oldOrderSnap = await getDoc(doc(db, "oldOrder", enquiryId));
          if (oldOrderSnap.exists()) setCurrentCollection("oldOrder");
        }
        return;
      }
      const data = snap.data();
      const newState = data.reqState || "New Request";
      setReqState(newState);
      if (data.userId) setCreatorUserId(data.userId);

      const stateEntry = otimelineData.find((e) => e.name === newState);
      if (stateEntry && currentCollection !== "oldOrder") {
        const stateNum = parseInt(stateEntry.id.replace("id", ""), 10);
        const correctCol =
          stateNum >= orderReceivedIdNumber && newState !== "Request Cancelled"
            ? "order"
            : "enquiry";
        if (correctCol !== currentCollection) setCurrentCollection(correctCol);
      }

      const changeEntry = exceptionTimelines.find(
        (e) => e.id.startsWith("change") && e.requestState === newState
      );
      if (changeEntry) {
        setShowChangeButtons(true);
        setChangeAction(changeEntry);
        setShowOptionDropdown(false);
        setOptionAction(null);
      } else {
        setShowChangeButtons(false);
        setChangeAction(null);
        const optionEntry = exceptionTimelines.find(
          (e) => e.id.startsWith("option") && e.requestState === newState
        );
        const isActionAllowed = processControls.some(
          (c) =>
            rolePrefixes.some((p) => c.id.startsWith(p)) &&
            c.application === "Small" &&
            c.activity === "Action" &&
            c.state === newState
        );
        const dvValid =
          deliveryModeRef.current === "PickUp"
            ? deliveryDateRef.current && deliveryTimeRef.current
            : selectedAddressRef.current &&
              deliveryDateRef.current &&
              deliveryTimeRef.current;
        if (optionEntry && isActionAllowed && (!isNormalUser || dvValid)) {
          setShowOptionDropdown(true);
          setOptionAction(optionEntry);
        } else {
          setShowOptionDropdown(false);
          setOptionAction(null);
        }
      }

      if (!isInitialized) {
        if (data.del_address) {
          setSelectedAddress(data.del_address);
          setInitialAddress(data.del_address);
          setAddressSet(true);
        }
        if (data.deliveryDate) {
          setDeliveryDate(data.deliveryDate);
          setInitialDeliveryDate(data.deliveryDate);
        }
        if (data.deliveryTime) {
          setDeliveryTime(data.deliveryTime);
          setInitialDeliveryTime(data.deliveryTime);
        }
        if (data.del_mode === false) {
          setDeliveryMode("PickUp");
          setInitialDeliveryMode("PickUp");
        }
        if (data.disp_msg) {
          setDispMsg(data.disp_msg);
          setInitialDispMsg(data.disp_msg);
        }
        if (data.msg_pos && data.name_cake) {
          setMsgPos(data.msg_pos);
          setInitialMsgPos(data.msg_pos);
        }
        if (data.rev_feed) {
          setReviewFeedback(data.rev_feed);
          setInitialReviewFeedback(data.rev_feed);
        }
        if (data.name_cake) {
          setNameCake(data.name_cake);
          setInitialNameCake(data.name_cake);
        }
        if (data.special_wish) {
          setSpecialWishes(data.special_wish);
          setInitialSpecialWishes(data.special_wish);
        }
        const isPickUp = data.del_mode === false;
        if (
          !data.deliveryDate ||
          !data.deliveryTime ||
          (!isPickUp && !data.del_address)
        )
          setDelivOpen(true);
        if (!data.cover || !data.tiers?.[0]?.cflavour) setSpecOpen(true);
        const ADDSPEC_EDITABLE_STATES = [
          "New Request",
          "Request Review",
          "Review Pending",
          "Review In-Progress",
        ];
        if (
          !data.name_cake &&
          !data.disp_msg &&
          ADDSPEC_EDITABLE_STATES.includes(newState)
        )
          setAddSpecOpen(true);
        setIsInitialized(true);
      }
    });
    return () => unsub();
  }, [
    enquiryId,
    currentCollection,
    otimelineData,
    orderReceivedIdNumber,
    exceptionTimelines,
    isInitialized,
    processControls,
  ]);

  // ── Editability from processControls ──────────────────────────────────
  useEffect(() => {
    if (
      !isOtimelineLoaded ||
      !isProcessControlsLoaded ||
      !reqState ||
      processControls.length === 0
    ) {
      setIsCakeReadOnly(true);
      return;
    }
    const prefix = userRole.toLowerCase();
    const applicable = processControls.filter(
      (c) =>
        c.id &&
        c.id.startsWith(prefix) &&
        c.application === "Large" &&
        c.activity === "Edit" &&
        c.state === reqState
    );
    setIsCakeReadOnly(applicable.length === 0);
  }, [
    reqState,
    userRole,
    processControls,
    isOtimelineLoaded,
    isProcessControlsLoaded,
  ]);

  // ── Fetch addresses ────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const targetId = isSuper && creatorUserId ? creatorUserId : user.uid;
    const load = async () => {
      try {
        const list = await readUserCollection("profile", targetId);
        const pd = list.length > 0 ? list[0] : null;
        const addrs = [];
        if (
          pd?.address_primary?.length === 3 &&
          pd.address_primary.every((v) => v?.trim())
        )
          addrs.push({ label: "Primary", value: pd.address_primary });
        if (
          pd?.address_secondary?.length === 3 &&
          pd.address_secondary.every((v) => v?.trim())
        )
          addrs.push({ label: "Secondary", value: pd.address_secondary });
        setAddresses(addrs);
        if (pd?.phone) setProfilePhone(pd.phone);
        if (pd?.id) setProfileDocId(pd.id);
        setProfileLoaded(true);
        setContactOpen(!pd?.phone);
      } catch (err) {
        console.error("Error fetching addresses", err);
      }
    };
    load();
  }, [user, creatorUserId, isSuper]);

  // ── Collapse / re-expand sections on reqState transitions ─────────────
  const prevReqStateRef = React.useRef("");
  useEffect(() => {
    if (!isInitialized || !reqState) return;
    if (prevReqStateRef.current === reqState) return;
    prevReqStateRef.current = reqState;
    if (reqState === "Review In-Progress") {
      setSpecOpen(false);
      setDelivOpen(false);
    } else if (
      [
        "New Request",
        "Request Review",
        "Review Pending",
        "Review Feedback",
      ].includes(reqState)
    ) {
      if (nameCake || dispMsg) setAddSpecOpen(true);
      setContactOpen(true);
    }
  }, [reqState, isInitialized]);

  // ── Track all changes ──────────────────────────────────────────────────
  useEffect(() => {
    if (!initialFormData) return;
    const cakeChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
      imgDeleted;
    const costChanged =
      JSON.stringify(imageCosts) !== JSON.stringify(initialImageCosts);
    const deliveryChanged =
      deliveryDate !== initialDeliveryDate ||
      deliveryTime !== initialDeliveryTime ||
      (selectedAddress || "") !== (initialAddress || "") ||
      deliveryMode !== initialDeliveryMode ||
      dispMsg !== initialDispMsg ||
      msgPos !== initialMsgPos ||
      nameCake !== initialNameCake ||
      specialWishes !== initialSpecialWishes ||
      delCost !== initialDelCost ||
      reviewFeedback !== initialReviewFeedback;
    const changed =
      cakeChanged ||
      costChanged ||
      deliveryChanged ||
      costest !== initialCostest;
    hasChangesRef.current = changed;
    setHasChanges(changed);
  }, [
    formData,
    initialFormData,
    imgDeleted,
    imageCosts,
    initialImageCosts,
    deliveryDate,
    initialDeliveryDate,
    deliveryTime,
    initialDeliveryTime,
    selectedAddress,
    initialAddress,
    deliveryMode,
    initialDeliveryMode,
    dispMsg,
    initialDispMsg,
    msgPos,
    initialMsgPos,
    nameCake,
    initialNameCake,
    specialWishes,
    initialSpecialWishes,
    delCost,
    initialDelCost,
    reviewFeedback,
    initialReviewFeedback,
    costest,
    initialCostest,
  ]);

  // ── Cake field handlers ────────────────────────────────────────────────
  const handleCoverChange = (value) => {
    const combo = coverCombinations.find((c) => c.cover === value);
    setFormData((prev) => ({
      ...prev,
      cover: value,
      frosting: combo?.frosting || "",
    }));
  };

  const getFillingOptions = (flavour) => {
    if (!flavour) return [];
    return [
      ...new Set(
        combinations
          .filter((c) => c.flavour === flavour)
          .map((c) => c.filling)
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  };

  const getSpongeOptions = (flavour) => {
    if (!flavour) return [];
    return [
      ...new Set(
        combinations
          .filter((c) => c.flavour === flavour)
          .flatMap((c) => (Array.isArray(c.sponge) ? c.sponge : [c.sponge]))
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  };

  const handleFlavourChange = (tierIdx, value) => {
    setFormData((prev) => {
      const fillings = [
        ...new Set(
          combinations
            .filter((c) => c.flavour === value)
            .map((c) => c.filling)
            .filter(Boolean)
        ),
      ];
      const sponges = [
        ...new Set(
          combinations
            .filter((c) => c.flavour === value)
            .flatMap((c) => (Array.isArray(c.sponge) ? c.sponge : [c.sponge]))
            .filter(Boolean)
        ),
      ];
      return {
        ...prev,
        tiers: prev.tiers.map((t, i) =>
          i !== tierIdx
            ? t
            : {
                ...t,
                cflavour: value,
                cfilling: fillings.length === 1 ? fillings[0] : "",
                sponge: sponges.length === 1 ? sponges[0] : "",
              }
        ),
      };
    });
  };

  const handleTierFieldChange = (tierIdx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      tiers: prev.tiers.map((t, i) =>
        i === tierIdx ? { ...t, [field]: value } : t
      ),
    }));
  };

  const handleDeleteImage = (idx) => {
    if (images.length <= 1) return;
    const newImgs = images.filter((_, i) => i !== idx);
    const newCosts = imageCosts.filter((_, i) => i !== idx);
    setImages(newImgs);
    setImageCosts(newCosts);
    setImgIndex(Math.min(imgIndex, Math.max(0, newImgs.length - 1)));
    setImgDeleted(true);
  };

  // ── Save all fields ────────────────────────────────────────────────────
  const handleSave = () => {
    if (!user || !enquiryId || !hasChanges) return;
    writeQueueRef.current = writeQueueRef.current.then(async () => {
      setSaving(true);
      try {
        const targetId = isNormalUser ? user.uid : creatorUserId || user.uid;
        const _clSave = buildChangeLog();
        const _d = new Date();
        const _cetDate = _d.toLocaleDateString("en-US", {
          timeZone: "Europe/Paris",
          month: "long",
          day: "numeric",
          year: "numeric",
        });
        const _cetTime = _d.toLocaleTimeString("en-GB", {
          timeZone: "Europe/Paris",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        const _cetNow = `${_cetDate} ${_cetTime}`;
        const _clTxt = _clSave.length
          ? "\n\n[" +
            _cetNow +
            "] " +
            _clSave
              .map((c) =>
                c.old && c.new
                  ? c.label + ": " + c.old + " > " + c.new
                  : c.label
              )
              .join(" | ")
          : "";
        const _baseFeed = (initialReviewFeedback || "").trim();
        const _currentFeed = reviewFeedback.trim();
        let _feedPart = null;
        if (_currentFeed === "") {
          _feedPart = null; // user cleared all comments
        } else if (_currentFeed === _baseFeed) {
          _feedPart = reviewFeedback; // no change
        } else if (_currentFeed.startsWith(_baseFeed)) {
          const _newPart = _currentFeed.slice(_baseFeed.length).trim();
          _feedPart =
            (_baseFeed ? initialReviewFeedback || "" : "") +
            "\n\n[" +
            _cetNow +
            "] " +
            _newPart;
        } else {
          _feedPart = reviewFeedback; // user edited existing text, save as-is
        }
        const finalFeed =
          _feedPart != null
            ? _feedPart + _clTxt || null
            : _clTxt.trim() || null;
        const updateData = {
          cover: formData.cover || null,
          frosting: formData.frosting || null,

          eggless: formData.eggless,
          glutenFree: formData.glutenFree,
          cflavour: formData.tiers[0]?.cflavour || null,
          cfilling: formData.tiers[0]?.cfilling || null,
          tiers: formData.tiers.map((t, i) => ({
            tier: i,
            cflavour: t.cflavour,
            cfilling: t.cfilling,
            sponge: t.sponge,
          })),
          quantity: formData.quantity
            ? (() => { const v = String(formData.quantity).replace(/\s*kg$/i, '').trim(); return v ? (isNaN(Number(v)) ? v : Number(v)) : null; })()
            : null,
          del_mode: deliveryMode === "Delivery",
          del_address:
            deliveryMode === "Delivery" ? selectedAddress || null : null,
          deliveryDate: deliveryDate || null,
          deliveryTime: deliveryTime || null,
          disp_msg: dispMsg || null,
          msg_pos: nameCake && msgPos ? msgPos : null,
          name_cake: nameCake || null,
          special_wish: specialWishes || null,
          del_cost: delCost ? Number(String(delCost).replace(/[^0-9.]/g, "")) || null : null,
          rev_feed: finalFeed,
        };
        if (imgDeleted) updateData.images = images;
        for (let i = 0; i < Math.max(images.length, 3); i++) {
          const key = i === 0 ? "ccost" : `ccost${i + 1}`;
          updateData[key] = i < images.length
            ? (imageCosts[i] ? String(imageCosts[i]).replace(/[^0-9.]/g, "") || null : null)
            : null;
        }
        if (isNormalUser) updateData.costest = costest ? String(costest).replace(/[^0-9.]/g, "") || null : null;
        await writeUserDocument(
          currentCollection,
          targetId,
          updateData,
          enquiryId
        );
        setInitialFormData(JSON.parse(JSON.stringify(formData)));
        setInitialImageCosts([...imageCosts]);
        setInitialCostest(costest);
        setInitialDeliveryDate(deliveryDate);
        setInitialDeliveryTime(deliveryTime);
        setInitialAddress(selectedAddress || "");
        setInitialDeliveryMode(deliveryMode);
        setInitialDispMsg(dispMsg);
        setInitialMsgPos(msgPos);
        setInitialNameCake(nameCake);
        setInitialSpecialWishes(specialWishes);
        setInitialDelCost(delCost);
        setInitialReviewFeedback(finalFeed || "");
        if (finalFeed && finalFeed !== reviewFeedback)
          setReviewFeedback(finalFeed);
        setImgDeleted(false);
        setHasChanges(false);
        hasChangesRef.current = false;
      } catch (err) {
        console.error("handleSave error", err);
        setError("Failed to save changes.");
      } finally {
        setSaving(false);
      }
    });
  };

  // ── Address helpers ────────────────────────────────────────────────────
  const handleAddAddress = async () => {
    if (
      !newAddress.streetName ||
      !newAddress.houseNumber ||
      !newAddress.pinCode
    )
      return;
    const addrStr = `${newAddress.streetName} ${newAddress.houseNumber}, ${newAddress.pinCode}`;
    const addrArr = [
      newAddress.streetName,
      newAddress.houseNumber,
      newAddress.pinCode,
    ];
    try {
      const targetId = isSuper && creatorUserId ? creatorUserId : user.uid;
      const list = await readUserCollection("profile", targetId);
      const pd = list.length > 0 ? list[0] : {};
      let upd;
      if (addAsHomeAddress) {
        upd = { address_primary: addrArr };
      } else {
        upd = !pd.address_primary
          ? { address_primary: addrArr }
          : { address_secondary: addrArr };
      }
      await writeUserDocument("profile", targetId, upd, pd.id || null);
      const primary = upd.address_primary || pd.address_primary;
      const secondary = addAsHomeAddress
        ? pd.address_secondary
        : upd.address_secondary || pd.address_secondary;
      const addrs = [];
      if (primary?.length === 3 && primary.every((v) => v?.trim()))
        addrs.push({ label: "Primary", value: primary });
      if (secondary?.length === 3 && secondary.every((v) => v?.trim()))
        addrs.push({ label: "Secondary", value: secondary });
      setAddresses(addrs);
      setSelectedAddress(addrStr);
      setAddressSet(true);
      setNewAddress({
        streetName: "",
        houseNumber: "",
        pinCode: "",
        pincodeRaw: "",
        cityRaw: "",
      });
      setAddAsHomeAddress(false);
      setShowAddAddressForm(false);
    } catch (err) {
      console.error("Error adding address", err);
    }
  };

  // -- Delivery cost auto-calculation -----------------------------------------
  const calcElcDistance = async (origin, destination) => {
    const backendUrl = _getBackendUrl();
    const res = await fetch(`${backendUrl}/distance-matrix`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ origin, destination }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Distance API error ${res.status}`);
    }
    const data = await res.json();
    if (!data.success || data.distanceValue == null) return { km: null };
    return { km: data.distanceValue / 1000, text: data.distance };
  };

  const getElcAdminAddress = async () => {
    if (elcAdminAddressRef.current) return elcAdminAddressRef.current;
    const cached = localStorage.getItem("organize_admin_address");
    if (cached) {
      elcAdminAddressRef.current = cached;
      return cached;
    }
    try {
      const users = await readGlobalProfiles("cUsers");
      const admin = users.find((u) => u.isadminuser);
      if (!admin) return null;
      const profiles = await readUserCollection("profile", admin.id);
      const p = profiles[0] || {};
      const addr = p.address || p.location || p.street || null;
      elcAdminAddressRef.current = addr;
      if (addr) localStorage.setItem("organize_admin_address", addr);
      return addr;
    } catch {
      return null;
    }
  };

  const autoFillDeliveryCost = async (address) => {
    if (!isSuper || !address || deliveryCharges.length === 0) return;
    const destination = address.toLowerCase().includes("germany")
      ? address
      : `${address}, Germany`;
    const adminAddr = await getElcAdminAddress();
    if (!adminAddr) return;
    const origin = adminAddr.toLowerCase().includes("germany")
      ? adminAddr
      : `${adminAddr}, Germany`;
    const cacheKey = `${origin}||${destination}`;
    try {
      setDelCostLoading(true);
      let result;
      if (elcDistCacheRef.current[cacheKey]) {
        result = elcDistCacheRef.current[cacheKey];
      } else {
        result = await calcElcDistance(origin, destination);
        elcDistCacheRef.current[cacheKey] = result;
      }
      if (result.km == null) return;
      setAutoCalcKm(parseFloat(result.km.toFixed(1)));
      // Only auto-fill the cost field if it hasn't been saved yet
      if (!initialDelCost) {
        const sorted = [...deliveryCharges].sort(
          (a, b) => parseFloat(a.distance) - parseFloat(b.distance)
        );
        const band =
          sorted.find((b) => result.km <= parseFloat(b.distance)) ||
          sorted[sorted.length - 1];
        if (band) setDelCost(String(band.cost));
      }
    } catch (err) {
      console.warn("ELC delivery cost calc failed:", err.message);
    } finally {
      setDelCostLoading(false);
    }
  };

  useEffect(() => {
    if (
      !isSuper ||
      deliveryMode !== "Delivery" ||
      !selectedAddress ||
      deliveryCharges.length === 0
    )
      return;
    // Skip if we already ran for this exact address this session
    if (lastAutoCalcAddrRef.current === selectedAddress) return;
    lastAutoCalcAddrRef.current = selectedAddress;
    autoFillDeliveryCost(selectedAddress);
  }, [
    selectedAddress,
    deliveryMode,
    deliveryCharges,
    isSuper,
    initialAddress,
    initialDelCost,
  ]);

  // ── Calendar helpers ───────────────────────────────────────────────────
  const getDateType = (dateStr) => {
    if (!dateStr || !isHolidayCalendarLoaded) return "weekday";
    const dow = new Date(dateStr).getDay();
    if (
      holidayCalendar.find(
        (e) => e.name === dateStr && e.id.startsWith("offday")
      )
    )
      return "offday";
    if (
      holidayCalendar.find(
        (e) => e.name === dateStr && e.id.startsWith("holiday")
      )
    )
      return "holiday";
    return dow === 0 || dow === 6 ? "weekend" : "weekday";
  };

  const getAvailableTimeSlots = (dt) => {
    if (!timeWindows || timeWindows.length === 0)
      return ["9:00 AM", "12:00 PM", "3:00 PM", "6:00 PM"];
    const prefix =
      dt === "weekday"
        ? "weekdays"
        : dt === "weekend" || dt === "holiday"
        ? "weekend"
        : null;
    if (!prefix) return [];
    const startHour = (t) => parseInt(t, 10) || 0;
    const slots = timeWindows
      .filter((e) => e.id && e.id.startsWith(prefix))
      .map((e) => e.name)
      .sort((a, b) => startHour(a) - startHour(b));
    return slots.length > 0
      ? slots
      : ["9:00 AM", "12:00 PM", "3:00 PM", "6:00 PM"];
  };

  const isDateDisabled = (dateStr) =>
    isHolidayCalendarLoaded &&
    !!holidayCalendar.find(
      (e) => e.name === dateStr && e.id.startsWith("offday")
    );

  const isDeliveryReadOnly = isCakeReadOnly;
  // Spec field editability for normal users
  const isCakeSpecReadOnly = isCakeReadOnly || isNormalUser;
  const isEgglessReadOnly =
    isCakeReadOnly || (isNormalUser && !profile?.isFromHome);
  // Additional Spec editable up to and including 'Review In-Progress' for all roles
  const _ripEntry = otimelineData.find((e) => e.name === "Review In-Progress");
  const _ripId = _ripEntry ? parseInt(_ripEntry.id.replace("id", ""), 10) : 0;
  const _addSpecCurEntry = otimelineData.find((e) => e.name === reqState);
  const _addSpecCurId = _addSpecCurEntry
    ? parseInt(_addSpecCurEntry.id.replace("id", ""), 10)
    : 0;
  const isAddSpecReadOnly = _addSpecCurId > _ripId;
  const dateType = getDateType(deliveryDate);
  const availableTimeSlots = getAvailableTimeSlots(dateType);

  // ── Build change log ───────────────────────────────────────────────────
  const buildChangeLog = () => {
    if (!initialFormData || isNormalUser) return [];
    const changes = [];
    if (
      (formData.cover || "") !== (initialFormData.cover || "") &&
      initialFormData.cover
    )
      changes.push({
        label: "Cover",
        old: initialFormData.cover,
        new: formData.cover || "",
      });
    formData.tiers.forEach((tier, i) => {
      const init = initialFormData.tiers?.[i] || {};
      const pfx = formData.tiers.length > 1 ? `Tier ${i + 1} ` : "";
      if ((tier.cflavour || "") !== (init.cflavour || "") && init.cflavour)
        changes.push({
          label: `${pfx}Flavour`,
          old: init.cflavour,
          new: tier.cflavour || "",
        });
      if ((tier.cfilling || "") !== (init.cfilling || "") && init.cfilling)
        changes.push({
          label: `${pfx}Filling`,
          old: init.cfilling,
          new: tier.cfilling || "",
        });
      if ((tier.sponge || "") !== (init.sponge || "") && init.sponge)
        changes.push({
          label: `${pfx}Sponge`,
          old: init.sponge,
          new: tier.sponge || "",
        });
    });
    if (
      (formData.quantity || "") !== (initialFormData.quantity || "") &&
      initialFormData.quantity
    )
      changes.push({
        label: "Quantity",
        old: String(initialFormData.quantity),
        new: String(formData.quantity || ""),
      });
    if (nameCake !== initialNameCake && initialNameCake)
      changes.push({
        label: "Name on Cake",
        old: initialNameCake,
        new: nameCake || "",
      });
    if (deliveryDate !== initialDeliveryDate && initialDeliveryDate)
      changes.push({
        label: "Date",
        old: initialDeliveryDate,
        new: deliveryDate || "",
      });
    if (deliveryTime !== initialDeliveryTime && initialDeliveryTime)
      changes.push({
        label: "Time",
        old: initialDeliveryTime,
        new: deliveryTime || "",
      });
    if (deliveryMode !== initialDeliveryMode && initialDeliveryMode)
      changes.push({
        label: "Delivery Mode",
        old: initialDeliveryMode,
        new: deliveryMode,
      });
    if ((selectedAddress || "") !== (initialAddress || "") && initialAddress)
      changes.push({
        label: "Address",
        old: initialAddress,
        new: selectedAddress || "",
      });
    if ((dispMsg || "") !== (initialDispMsg || "") && initialDispMsg)
      changes.push({
        label: "Message",
        old: initialDispMsg,
        new: dispMsg || "",
      });
    if (
      (specialWishes || "") !== (initialSpecialWishes || "") &&
      initialSpecialWishes
    )
      changes.push({
        label: "Special Wishes",
        old: initialSpecialWishes,
        new: specialWishes || "",
      });
    imageCosts.forEach((cost, i) => {
      const initCost = initialImageCosts?.[i];
      if ((cost || "") !== (initCost || ""))
        changes.push({ _priceChange: true, isNew: !initCost });
    });
    const delCostChanged = (delCost || "") !== (initialDelCost || "");
    if (delCostChanged)
      changes.push({ _priceChange: true, isNew: !initialDelCost });
    // Collapse all price changes into one entry
    const priceEntries = changes.filter((c) => c._priceChange);
    const otherEntries = changes.filter((c) => !c._priceChange);
    if (priceEntries.length > 0) {
      const allNew = priceEntries.every((c) => c.isNew);
      otherEntries.push({
        label: allNew ? "Price has been set" : "Price has been updated",
      });
    }
    return otherEntries;
  };
  const changeLog = buildChangeLog();
  const hasCosts = imageCosts.some((c) => c && String(c).trim() !== "");
  const _rpEntry = otimelineData.find((e) => e.name === "Review Pending");
  const _curEntry = otimelineData.find((e) => e.name === reqState);
  const _rpId = _rpEntry
    ? parseInt(_rpEntry.id.replace("id", ""), 10)
    : Infinity;
  const _curId = _curEntry ? parseInt(_curEntry.id.replace("id", ""), 10) : 0;
  const isUpToReviewPending = _curId <= _rpId;

  const isPickUpMode = deliveryMode === "PickUp";
  const deliveryDetailsValid = isPickUpMode
    ? deliveryDate && deliveryTime
    : selectedAddress && deliveryDate && deliveryTime;

  const canEnableButton = (stateToCheck = reqState) => {
    if (isButtonDisabled) return false;
    return processControls.some(
      (c) =>
        rolePrefixes.some((p) => c.id.startsWith(p)) &&
        c.application === "Small" &&
        c.activity === "Action" &&
        c.state === stateToCheck
    );
  };

  const getStopOrPauseAction = (s) => {
    const stop = exceptionTimelines.find(
      (e) => e.id.startsWith("stop") && e.requestState === s
    );
    if (stop) return { type: "stop", state: stop.showState1 };
    const pause = exceptionTimelines.find(
      (e) => e.id.startsWith("pause") && e.requestState === s
    );
    if (pause) return { type: "pause", state: pause.showState1 };
    return null;
  };

  const computeNextStateAndButtonText = (s) => {
    const action = getStopOrPauseAction(s);
    if (action)
      return {
        nextState: action.state,
        buttonText: action.state,
        isStopAction: action.type === "stop",
      };
    if (!s || otimelineData.length === 0)
      return {
        nextState: "Request Review",
        buttonText: "Request Review",
        isStopAction: false,
      };
    const entry = otimelineData.find((e) => e.name === s);
    if (!entry)
      return {
        nextState: "Request Review",
        buttonText: "Request Review",
        isStopAction: false,
      };
    const nextEntry = otimelineData.find(
      (e) => e.id === `id${parseInt(entry.id.replace("id", ""), 10) + 1}`
    );
    const nextState = nextEntry ? nextEntry.name : "Request Review";
    return { nextState, buttonText: nextState, isStopAction: false };
  };

  const canEnableDelete = (stateToCheck = reqState) => {
    return processControls.some(
      (c) =>
        rolePrefixes.some((p) => c.id.startsWith(p)) &&
        c.application === "Small" &&
        c.activity === "Delete" &&
        c.state === stateToCheck
    );
  };

  const { nextState, buttonText, isStopAction } =
    computeNextStateAndButtonText(reqState);
  // When cost inputs are visible (Super + editable), every image must have a cost filled
  // costest counts as a valid cost for the first image slot (will be promoted to ccost on Review In-Progress)
  const isCostMissing =
    isSuper &&
    !isCakeReadOnly &&
    images.length > 0 &&
    images.some((_, i) => {
      const c = imageCosts[i];
      if (i === 0 && (!c || String(c).trim() === "") && costest) return false;
      return !c || String(c).trim() === "";
    });
  // Mandatory form fields (only enforced when the form is editable)
  const mandatoryFieldsFilled =
    isCakeReadOnly ||
    (!!formData.cover &&
      !!formData.tiers[0]?.cflavour &&
      !!formData.tiers[0]?.cfilling &&
      !!formData.tiers[0]?.sponge);
  // Status button enabled by reqState + delivery details + mandatory fields + not currently saving/pending
  const phoneChanged =
    showAddPhoneForm &&
    phoneInput !== stripAnyISD(profilePhone);
  const isPhoneProvided =
    !isNormalUser || !profileLoaded || profilePhone || phoneVerified;
  const isEnquiryButtonEnabled =
    canEnableButton() &&
    (!isNormalUser || deliveryDetailsValid) &&
    mandatoryFieldsFilled &&
    isPhoneProvided &&
    !saving &&
    !hasChanges;

  const _updateCollectionAfterStateChange = async (
    newReqState,
    targetUserId,
    updateData
  ) => {
    // Flush any pending debounced save immediately so image deletions etc. are committed first
    if (hasChangesRef.current) {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      handleSaveRef.current();
    }
    await writeQueueRef.current; // wait for the (now-queued) save to complete
    const finalData = { ...updateData };
    if (!("rev_feed" in finalData) && pendingFeedRef.current != null) {
      finalData.rev_feed = pendingFeedRef.current;
    }
    pendingFeedRef.current = null;

    // When entering 'Review In-Progress', promote costest ? ccost (if ccost empty) and clear costest
    if (newReqState === "Review In-Progress") {
      const currentCcost = imageCosts[0]
        ? String(imageCosts[0])
            .replace(/^€|^EUR/i, "")
            .trim()
        : "";
      const currentCostest = costest
        ? String(costest)
            .replace(/^€|^EUR/i, "")
            .trim()
        : "";
      if (!currentCcost && currentCostest) {
        finalData.ccost = currentCostest;
        const updatedCosts = imageCosts.map((c, i) =>
          i === 0 ? currentCostest : c
        );
        setImageCosts(updatedCosts);
        setInitialImageCosts([...updatedCosts]);
      }
      finalData.costest = null;
      setCostest(null);
      setInitialCostest(null);
    }
    const stateEntry = otimelineData.find((e) => e.name === newReqState);
    const dipEntry = otimelineData.find(
      (e) => e.name?.toLowerCase() === "delivery in-progress"
    );
    const dipIdNum = dipEntry
      ? parseInt(dipEntry.id.replace("id", ""), 10)
      : null;
    const newStateIdNum = stateEntry
      ? parseInt(stateEntry.id.replace("id", ""), 10)
      : null;
    const shouldMoveToOldOrder =
      currentCollection === "order" &&
      ((dipIdNum !== null &&
        newStateIdNum !== null &&
        newStateIdNum >= dipIdNum &&
        newReqState !== "Order On-hold" &&
        newReqState !== "Order Cancelled") ||
        newReqState === "Order Cancelled");
    if (shouldMoveToOldOrder) {
      await writeUserDocument("order", targetUserId, finalData, enquiryId);
      await moveUserDocument("order", "oldOrder", targetUserId, enquiryId);
      setCurrentCollection("oldOrder");
    } else if (
      currentCollection === "enquiry" &&
      newReqState === "Order Received"
    ) {
      await moveUserDocument("enquiry", "order", targetUserId, enquiryId);
      await writeUserDocument("order", targetUserId, finalData, enquiryId);
      setCurrentCollection("order");
      if (onProfileMoved) onProfileMoved(enquiryId);
    } else {
      await writeUserDocument(
        currentCollection,
        targetUserId,
        finalData,
        enquiryId
      );
      if (stateEntry) {
        const num = parseInt(stateEntry.id.replace("id", ""), 10);
        if (
          num >= orderReceivedIdNumber &&
          newReqState !== "Request Cancelled" &&
          currentCollection === "enquiry"
        ) {
          await moveUserDocument("enquiry", "order", targetUserId, enquiryId);
          await writeUserDocument("order", targetUserId, finalData, enquiryId);
          setCurrentCollection("order");
          if (onProfileMoved) onProfileMoved(enquiryId);
        } else if (
          (num < orderReceivedIdNumber ||
            newReqState === "Request Cancelled") &&
          currentCollection === "order"
        ) {
          await moveUserDocument("order", "enquiry", targetUserId, enquiryId);
          await writeUserDocument(
            "enquiry",
            targetUserId,
            finalData,
            enquiryId
          );
          setCurrentCollection("enquiry");
        }
      }
    }
  };

  const COMMENT_REQUIRED_STATES = ["Order On-hold", "Order Cancelled"];

  const requireCommentThenRun = (targetState, fn) => {
    pendingStateChangeFnRef.current = fn;
    setPendingStateLabel(targetState);
    setRequiredCommentText("");
    setShowCommentRequiredPopup(true);
  };

  const handleCommentRequiredSubmit = async () => {
    const comment = requiredCommentText.trim();
    if (!comment) return;
    try {
      const targetId = isNormalUser ? user.uid : creatorUserId || user.uid;
      const _d = new Date();
      const _cetDate = _d.toLocaleDateString("en-US", {
        timeZone: "Europe/Paris",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
      const _cetTime = _d.toLocaleTimeString("en-GB", {
        timeZone: "Europe/Paris",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const _cetNow = `${_cetDate} ${_cetTime}`;
      const existingFeed = (reviewFeedback || "").trim();
      const newFeed = existingFeed
        ? `${existingFeed}\n\n[${_cetNow}] ${comment}`
        : `[${_cetNow}] ${comment}`;
      pendingFeedRef.current = newFeed;
      setReviewFeedback(newFeed);
      setInitialReviewFeedback(newFeed);
      setShowCommentRequiredPopup(false);
      setRequiredCommentText("");
      if (pendingStateChangeFnRef.current)
        await pendingStateChangeFnRef.current();
      pendingStateChangeFnRef.current = null;
    } catch (err) {
      console.error("Comment required submit error:", err);
      showToast(`Failed: ${err.message}`);
      setShowCommentRequiredPopup(false);
    }
  };

  const handleRequestReview = async (e) => {
    e.stopPropagation();
    if (!isEnquiryButtonEnabled) {
      setShowPopup(true);
      return;
    }
    if (isCostMissing) {
      setShowCostMissingPopup(true);
      return;
    }
    if (
      reqState === "Review Pending" &&
      deliveryMode === "Delivery" &&
      !String(delCost || "").trim()
    ) {
      setShowDelCostMissingPopup(true);
      return;
    }
    // Normal user moving from New Request ? Request Review: block if another ongoing enquiry exists
    if (isNormalUser && reqState === "New Request") {
      const ONGOING_STATES = [
        "Request Review",
        "Review Pending",
        "Review In-Progress",
        "Review Feedback",
      ];
      const db = getDb();
      const userQ = where("userId", "==", user.uid);
      const [enqSnap, ordSnap] = await Promise.all([
        getDocs(query(collection(db, "enquiry"), userQ)),
        getDocs(query(collection(db, "order"), userQ)),
      ]);
      const hasOngoing = [...enqSnap.docs, ...ordSnap.docs].some((d) => {
        if (d.id === enquiryId) return false;
        const s = d.data().reqState;
        return s && ONGOING_STATES.includes(s);
      });
      if (hasOngoing) {
        setShowOngoingEnquiryModal(true);
        return;
      }
    }
    if (nextState === "Give Feedback") {
      setShowFeedbackPopup(true);
      return;
    }
    if (COMMENT_REQUIRED_STATES.includes(nextState)) {
      requireCommentThenRun(nextState, async () => {
        const targetUserId = isNormalUser ? user.uid : creatorUserId;
        if (!targetUserId) throw new Error("Unable to determine the user ID");
        await _updateCollectionAfterStateChange(nextState, targetUserId, {
          reqState: nextState,
        });
        setReqState(nextState);
        if (isStopAction) setIsButtonDisabled(true);
        const optionEntry = exceptionTimelines.find(
          (e) => e.id.startsWith("option") && e.requestState === nextState
        );
        const isActionAllowed = processControls.some(
          (c) =>
            rolePrefixes.some((p) => c.id.startsWith(p)) &&
            c.application === "Small" &&
            c.activity === "Action" &&
            c.state === nextState
        );
        if (
          optionEntry &&
          isActionAllowed &&
          (!isNormalUser || deliveryDetailsValid) &&
          !isStopAction
        ) {
          setShowOptionDropdown(true);
          setOptionAction(optionEntry);
        } else {
          setShowOptionDropdown(false);
          setOptionAction(null);
        }
        if (
          onClose &&
          nextState !== "Review Pending" &&
          nextState !== "Review Feedback"
        )
          onClose();
      });
      return;
    }
    try {
      const targetUserId = isNormalUser ? user.uid : creatorUserId;
      if (!targetUserId) throw new Error("Unable to determine the user ID");
      await _updateCollectionAfterStateChange(nextState, targetUserId, {
        reqState: nextState,
      });
      setReqState(nextState);
      if (isStopAction) setIsButtonDisabled(true);
      const optionEntry = exceptionTimelines.find(
        (e) => e.id.startsWith("option") && e.requestState === nextState
      );
      const isActionAllowed = processControls.some(
        (c) =>
          rolePrefixes.some((p) => c.id.startsWith(p)) &&
          c.application === "Small" &&
          c.activity === "Action" &&
          c.state === nextState
      );
      if (
        optionEntry &&
        isActionAllowed &&
        (!isNormalUser || deliveryDetailsValid) &&
        !isStopAction
      ) {
        setShowOptionDropdown(true);
        setOptionAction(optionEntry);
      } else {
        setShowOptionDropdown(false);
        setOptionAction(null);
      }
      if (
        onClose &&
        nextState !== "Review Pending" &&
        nextState !== "Review Feedback"
      )
        onClose();
    } catch (err) {
      console.error("Error updating reqState:", err);
      showToast(`Failed: ${err.message}`);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.stopPropagation();
    if (feedbackRating === 0) {
      showToast("Please select a star rating.");
      return;
    }
    if (feedbackRating < 4 && feedbackComment.trim() === "") {
      showToast("Please describe your experience.");
      return;
    }
    try {
      const targetUserId = isNormalUser ? user.uid : creatorUserId;
      if (!targetUserId) throw new Error("Unable to determine the user ID");
      const updateData = {
        reqState: nextState,
        feedback: {
          rating: feedbackRating,
          comment: feedbackComment.trim() || null,
          submittedAt: serverTimestamp(),
        },
      };
      await _updateCollectionAfterStateChange(
        nextState,
        targetUserId,
        updateData
      );
      setReqState(nextState);
      setShowFeedbackPopup(false);
      setFeedbackRating(0);
      setFeedbackComment("");
      if (
        onClose &&
        nextState !== "Review Pending" &&
        nextState !== "Review Feedback"
      )
        onClose();
    } catch (err) {
      console.error("Error submitting feedback:", err);
      showToast(`Failed: ${err.message}`);
      setShowFeedbackPopup(false);
    }
  };

  const handleAcceptConfirm = async () => {
    if (!changeAction) return;
    const newReqState = changeAction.showState1;
    setShowAcceptConfirmModal(false);
    setAcceptCheck1(false);
    setAcceptCheck2(false);
    setAcceptCheck3(false);
    try {
      const targetUserId = isNormalUser ? user.uid : creatorUserId;
      if (!targetUserId) throw new Error("Unable to determine the user ID");
      await _updateCollectionAfterStateChange(newReqState, targetUserId, {
        reqState: newReqState,
      });
      setReqState(newReqState);
      setShowChangeButtons(false);
      setChangeAction(null);
      if (
        onClose &&
        newReqState !== "Review Pending" &&
        newReqState !== "Review Feedback"
      )
        onClose();
    } catch (err) {
      console.error("Error accepting:", err);
      showToast(`Failed: ${err.message}`);
    }
  };

  const handleAccept = async (e) => {
    e.stopPropagation();
    if (!changeAction) return;
    if (isNormalUser && images.length > 1) {
      setShowImageCheckPopup(true);
      return;
    }
    // When accepting from 'Review Feedback', show the confirmation modal with checkboxes
    if (reqState === "Review Feedback") {
      setAcceptCheck1(false);
      setAcceptCheck2(false);
      setAcceptCheck3(false);
      setShowAcceptConfirmModal(true);
      return;
    }
    const newReqState = changeAction.showState1;
    if (COMMENT_REQUIRED_STATES.includes(newReqState)) {
      requireCommentThenRun(newReqState, async () => {
        const targetUserId = isNormalUser ? user.uid : creatorUserId;
        if (!targetUserId) throw new Error("Unable to determine the user ID");
        await _updateCollectionAfterStateChange(newReqState, targetUserId, {
          reqState: newReqState,
        });
        setReqState(newReqState);
        setShowChangeButtons(false);
        setChangeAction(null);
        if (
          onClose &&
          newReqState !== "Review Pending" &&
          newReqState !== "Review Feedback"
        )
          onClose();
      });
      return;
    }
    try {
      const targetUserId = isNormalUser ? user.uid : creatorUserId;
      if (!targetUserId) throw new Error("Unable to determine the user ID");
      await _updateCollectionAfterStateChange(newReqState, targetUserId, {
        reqState: newReqState,
      });
      setReqState(newReqState);
      setShowChangeButtons(false);
      setChangeAction(null);
      if (
        onClose &&
        newReqState !== "Review Pending" &&
        newReqState !== "Review Feedback"
      )
        onClose();
    } catch (err) {
      console.error("Error accepting:", err);
      showToast(`Failed: ${err.message}`);
    }
  };

  const handleRejectClick = (e) => {
    e.stopPropagation();
    setShowRejectPopup(true);
  };

  const handleRejectConfirm = async () => {
    if (!changeAction) return;
    const newReqState = changeAction.showState2;
    if (COMMENT_REQUIRED_STATES.includes(newReqState)) {
      setShowRejectPopup(false);
      requireCommentThenRun(newReqState, async () => {
        const targetUserId = isNormalUser ? user.uid : creatorUserId;
        if (!targetUserId) throw new Error("Unable to determine the user ID");
        await _updateCollectionAfterStateChange(newReqState, targetUserId, {
          reqState: newReqState,
        });
        setReqState(newReqState);
        setShowChangeButtons(false);
        setChangeAction(null);
        if (
          onClose &&
          newReqState !== "Review Pending" &&
          newReqState !== "Review Feedback"
        )
          onClose();
      });
      return;
    }
    try {
      const targetUserId = isNormalUser ? user.uid : creatorUserId;
      if (!targetUserId) throw new Error("Unable to determine the user ID");
      await _updateCollectionAfterStateChange(newReqState, targetUserId, {
        reqState: newReqState,
      });
      setReqState(newReqState);
      setShowChangeButtons(false);
      setChangeAction(null);
      setShowRejectPopup(false);
      if (newReqState === "Request Cancelled") {
        setReviewFeedback("");
        setInitialReviewFeedback("");
      }
      if (
        onClose &&
        newReqState !== "Review Pending" &&
        newReqState !== "Review Feedback"
      )
        onClose();
    } catch (err) {
      console.error("Error rejecting:", err);
      showToast(`Failed: ${err.message}`);
      setShowRejectPopup(false);
    }
  };

  const handleOptionSelect = async (e) => {
    e.stopPropagation();
    const selectedReqState = e.target.value;
    if (!selectedReqState || !optionAction) return;
    if (COMMENT_REQUIRED_STATES.includes(selectedReqState)) {
      requireCommentThenRun(selectedReqState, async () => {
        const targetUserId = isNormalUser ? user.uid : creatorUserId;
        if (!targetUserId) throw new Error("Unable to determine the user ID");
        let finalReqState = selectedReqState;
        let isStopActionTriggered = false;
        const action = getStopOrPauseAction(selectedReqState);
        if (action) {
          finalReqState = action.state;
          isStopActionTriggered = action.type === "stop";
        }
        await _updateCollectionAfterStateChange(finalReqState, targetUserId, {
          reqState: finalReqState,
        });
        setReqState(finalReqState);
        setShowOptionDropdown(false);
        setOptionAction(null);
        if (isStopActionTriggered) setIsButtonDisabled(true);
        if (
          onClose &&
          finalReqState !== "Review Pending" &&
          finalReqState !== "Review Feedback"
        )
          onClose();
      });
      return;
    }
    try {
      const targetUserId = isNormalUser ? user.uid : creatorUserId;
      if (!targetUserId) throw new Error("Unable to determine the user ID");
      let finalReqState = selectedReqState;
      let isStopActionTriggered = false;
      const action = getStopOrPauseAction(selectedReqState);
      if (action) {
        finalReqState = action.state;
        isStopActionTriggered = action.type === "stop";
      }
      await _updateCollectionAfterStateChange(finalReqState, targetUserId, {
        reqState: finalReqState,
      });
      setReqState(finalReqState);
      setShowOptionDropdown(false);
      setOptionAction(null);
      if (isStopActionTriggered) setIsButtonDisabled(true);
      if (
        onClose &&
        finalReqState !== "Review Pending" &&
        finalReqState !== "Review Feedback"
      )
        onClose();
    } catch (err) {
      console.error("Error option select:", err);
      showToast(`Failed: ${err.message}`);
    }
  };

  // ── Auto-save (debounced 1.5 s after last change) ─────────────────────────

  // Phone verification handlers
  const handleSendPhoneCode = async () => {
    if (!phoneInput || phoneInput.length < 5) return;
    setPhoneSendingCode(true);
    setPhoneError("");
    try {
      const targetId = isSuper && creatorUserId ? creatorUserId : user.uid;
      const fullPhone = isdCode + phoneInput;
      const response = await fetch(
        "https://us-central1-pcakedatastore.cloudfunctions.net/sendPhoneVerificationCode",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: targetId, phoneNumber: fullPhone }),
        }
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error?.message || "Failed to send code");
      setPhoneCodeSent(true);
      setPhoneError("Code sent via SMS.");
      setPhoneResendCountdown(30);
      const interval = setInterval(() => {
        setPhoneResendCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setPhoneError(err.message || "Failed to send verification code.");
    } finally {
      setPhoneSendingCode(false);
    }
  };

  const handleVerifyPhoneCode = async (codeOverride) => {
    const code = codeOverride ?? phoneCodeInput;
    if (code.length !== 6) return;
    setPhoneVerifying(true);
    setPhoneError("");
    try {
      const targetId = isSuper && creatorUserId ? creatorUserId : user.uid;
      const fullPhone = isdCode + phoneInput;
      const response = await fetch(
        "https://us-central1-pcakedatastore.cloudfunctions.net/verifyPhoneCode",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: targetId,
            phoneNumber: fullPhone,
            code,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error?.message || "Verification failed");
      setPhoneVerified(true);
      setPhoneError("");
    } catch (err) {
      setPhoneError("invalid");
    } finally {
      setPhoneVerifying(false);
    }
  };

  const handleConfirmPhone = async () => {
    if (phoneChanged) {
      if (!phoneVerified) return;
      try {
        const targetId = isSuper && creatorUserId ? creatorUserId : user.uid;
      const fullPhone = isdCode + phoneInput;
        const list = await readUserCollection("profile", targetId);
        const pd = list.length > 0 ? list[0] : {};
        await writeUserDocument(
          "profile",
          targetId,
          { phone: fullPhone },
          pd.id || null
        );
        setProfilePhone(fullPhone);
        setProfileDocId(pd.id || null);
      } catch (err) {
        setPhoneError("Failed to save phone number.");
        return;
      }
    }
    setShowAddPhoneForm(false);
    setPhoneCodeSent(false);
    setPhoneCodeInput("");
    setPhoneInput("");
    setPhoneVerified(false);
    setPhoneError("");
    setAddAsPrimaryPhone(false);
  };

  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (!hasChanges || !isInitialized) return;
    hasChangesRef.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      handleSaveRef.current();
    }, 1500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [
    hasChanges,
    formData,
    imageCosts,
    deliveryDate,
    deliveryTime,
    selectedAddress,
    deliveryMode,
    dispMsg,
    msgPos,
    nameCake,
    specialWishes,
    delCost,
    reviewFeedback,
    imgDeleted,
    isInitialized,
  ]);

  // ── Feedback renderer (colours only the most recent change-log entry) ────
  const renderFeedback = (text) => {
    if (!text) return null;
    const chunks = text.split("\n\n");
    const changeIndices = chunks.reduce((acc, chunk, i) => {
      if (
        /^\[\d{2}\/\d{2}\/\d{4}|^\[[A-Z][a-z]+ \d{1,2}, \d{4}/.test(chunk) ||
        chunk.startsWith("Change Details:")
      )
        acc.push(i);
      return acc;
    }, []);
    const lastIdx =
      changeIndices.length > 0 ? changeIndices[changeIndices.length - 1] : -1;
    return chunks.map((chunk, i) => (
      <React.Fragment key={i}>
        {i > 0 && "\n\n"}
        {i === lastIdx ? (
          <span style={{ color: "#fa4848" }}>{chunk}</span>
        ) : (
          chunk
        )}
      </React.Fragment>
    ));
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="unified-profile-card unified-profile-user">
      {error && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
          }}
        >
          <div className="elc-error-toast">
            <button
              onClick={() => setError(null)}
              className="elc-error-toast-close"
            >
              <FaTimes />
            </button>
            <p style={{ margin: 0, fontSize: "13px" }}>{error}</p>
          </div>
        </div>
      )}

      <div className="profile-details unified-profile-user-details elc-card">
        <button
          className="save-close-btn close-btn"
          onClick={() => {
            // Flush any pending debounced save before closing
            if (hasChangesRef.current) {
              if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
              }
              handleSaveRef.current();
            }
            onClose();
          }}
          type="button"
          title="Close"
          disabled={saving}
        >
          <FaTimes />
        </button>
        {/* Theme header */}
        {profile?.theme && (
          <div className="elc-theme-header">
            {profile.theme}
          </div>
        )}

        {/* ── IMAGE CAROUSEL ── */}
        <div className="form-group image-section">
          <div className="profile-image-section">
            <div className="image-carousel-container">
              {images.length <= 1 ? (
                images.length > 0 && images[0] ? (
                  <div className="image-carousel">
                    <div className="image-wrapper">
                      <img
                        src={images[0]}
                        alt="Cake"
                        className="profile-image"
                      />
                    </div>
                  </div>
                ) : null
              ) : (
                <>
                  <button
                    type="button"
                    className="carousel-arrow unified-profile-user-carousel-arrow left"
                    onClick={() => setImgIndex((i) => Math.max(0, i - 1))}
                    disabled={imgIndex === 0}
                  >
                    <FaArrowLeft />
                  </button>
                  <div className="image-carousel">
                    {images[imgIndex] && (
                      <div
                        className="image-wrapper"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={images[imgIndex]}
                          alt="Cake"
                          className="profile-image"
                        />
                        {(isSuper ||
                          !isCakeReadOnly ||
                          (showChangeButtons && images.length > 1)) && (
                          <button
                            type="button"
                            onClick={() => handleDeleteImage(imgIndex)}
                            className="elc-img-delete-btn"
                          >
                            <FaTrash size={11} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="carousel-arrow unified-profile-user-carousel-arrow right"
                    onClick={() =>
                      setImgIndex((i) => Math.min(images.length - 1, i + 1))
                    }
                    disabled={imgIndex === images.length - 1}
                  >
                    <FaArrowRight />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── COLLAPSIBLE SPECIFICATION ── */}
        <div className="elc-spec-toggle" onClick={() => setSpecOpen((o) => !o)}>
          <span className="elc-spec-toggle-label">Specification</span>
          {specOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
        </div>

        {specOpen && (
          <div className="elc-spec-body">
            {/* Cover & Frosting */}
            <div className="form-group-row">
              <div className="form-group-item">
                <label>Cover:</label>
                <div className="field-value">
                  {isCakeSpecReadOnly ? (
                    <span>{formData.cover || ""}</span>
                  ) : (
                    <select
                      value={formData.cover || ""}
                      onChange={(e) => handleCoverChange(e.target.value)}
                    >
                      <option value="">Select</option>
                      {coverOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              <div className="form-group-item">
                <label>Frosting:</label>
                <div className="field-value">
                  <span>{formData.frosting || ""}</span>
                </div>
              </div>
            </div>
            <hr className="dotted-line" />

            {/* Sponge + Eggless + GlutenFree */}
            <div className="form-group-row elc-sponge-diet-row">
              <div
                className="form-group-item"
                style={{ flex: "0 0 auto", minWidth: "120px" }}
              >
                <label>Sponge:</label>
                <div className="field-value">
                  {isCakeSpecReadOnly || !formData.tiers[0]?.cflavour ? (
                    <span>{formData.tiers[0]?.sponge || ""}</span>
                  ) : (
                    <select
                      value={formData.tiers[0]?.sponge || ""}
                      onChange={(e) =>
                        handleTierFieldChange(0, "sponge", e.target.value)
                      }
                    >
                      <option value="">Select</option>
                      {getSpongeOptions(formData.tiers[0]?.cflavour).map(
                        (o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        )
                      )}
                    </select>
                  )}
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <div style={{ display: "flex", width: "100%" }}>
                  <div
                    style={{
                      width: "120px",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        margin: 0,
                      }}
                    >
                      {!isEgglessReadOnly && (
                        <input
                          type="checkbox"
                          checked={formData.eggless}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              eggless: e.target.checked,
                            }))
                          }
                          style={{
                            margin: 0,
                            width: "16px",
                            height: "16px",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          minWidth: "68px",
                        }}
                      >
                        Eggless
                      </span>
                      {isEgglessReadOnly && (
                        <span
                          style={{
                            fontWeight: "normal",
                            fontSize: "12px",
                            marginLeft: "4px",
                            flexShrink: 0,
                          }}
                        >
                          {formData.eggless ? ": Yes" : ": No"}
                        </span>
                      )}
                    </label>
                  </div>
                </div>
                <div style={{ display: "flex", width: "100%" }}>
                  <div
                    style={{
                      width: "120px",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        margin: 0,
                      }}
                    >
                      {!isEgglessReadOnly && (
                        <input
                          type="checkbox"
                          checked={formData.glutenFree}
                          onChange={(e) => {
                            const gf = e.target.checked;
                            setFormData((p) => ({ ...p, glutenFree: gf }));
                            if (isNormalUser || isSuper) {
                              const pSuf =
                                _ELC_LABEL_TO_SUFFIX[formData.quantity] || "";
                              if (
                                pSuf &&
                                Object.keys(cakeChargeMap).length > 0
                              ) {
                                const isTier = formData.tiers.length > 1;
                                const seg = isTier ? "T" : "N";
                                const bSuf = isTier ? "15" : "10";
                                const t1 = formData.tiers[0] || {};
                                const comboM =
                                  combinations.find(
                                    (c) =>
                                      c.flavour === t1.cflavour &&
                                      c.filling === t1.cfilling
                                  ) ||
                                  combinations.find(
                                    (c) => c.flavour === t1.cflavour
                                  );
                                const isSpecialF = comboM?.priceSegment === "S";
                                const coverM = coverCombinations.find(
                                  (c) => c.cover === formData.cover
                                );
                                const coverPS =
                                  coverM?.priceSegment === "B" ? "B" : "F";
                                const baseND = (pfx, sfx) =>
                                  parseFloat(cakeChargeMap[pfx + bSuf] || 0) +
                                  (sfx !== bSuf
                                    ? parseFloat(cakeChargeMap[pfx + sfx] || 0)
                                    : 0);
                                const flatS = (pfx, sfx) =>
                                  parseFloat(cakeChargeMap[pfx + sfx] || 0);
                                const val = (
                                  baseND(seg, pSuf) +
                                  (isSpecialF ? flatS("S", pSuf) : 0) +
                                  (gf ? flatS("S", pSuf) : 0) +
                                  (!isTier && coverPS === "B"
                                    ? flatS("B", pSuf)
                                    : 0)
                                ).toFixed(2);
                                setCostest(`€${val}`);
                              } else {
                                setCostest(null);
                              }
                            }
                          }}
                          style={{
                            margin: 0,
                            width: "16px",
                            height: "16px",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <span
                        style={{
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          minWidth: "68px",
                        }}
                      >
                        Gluten Free
                      </span>
                      {isEgglessReadOnly && (
                        <span
                          style={{
                            fontWeight: "normal",
                            fontSize: "12px",
                            marginLeft: "4px",
                            flexShrink: 0,
                          }}
                        >
                          {formData.glutenFree ? ": Yes" : ": No"}
                        </span>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <hr className="dotted-line" />

            {/* Tier rows (Flavour + Filling) */}
            {formData.tiers.map((tier, idx) => {
              const fillings = getFillingOptions(tier.cflavour);
              return (
                <React.Fragment key={idx}>
                  {formData.tiers.length > 1 && (
                    <div className="elc-tier-label">Tier {idx + 1}</div>
                  )}
                  <div className="form-group-row">
                    <div className="form-group-item">
                      <label>Flavour:</label>
                      <div className="field-value">
                        {isCakeSpecReadOnly ? (
                          <span>{tier.cflavour || ""}</span>
                        ) : (
                          <select
                            value={tier.cflavour || ""}
                            onChange={(e) =>
                              handleFlavourChange(idx, e.target.value)
                            }
                          >
                            <option value="">Select</option>
                            {flavourOptions.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                    <div className="form-group-item">
                      <label>Filling:</label>
                      <div className="field-value">
                        {isCakeSpecReadOnly || !tier.cflavour ? (
                          <span>{tier.cfilling || ""}</span>
                        ) : (
                          <select
                            value={tier.cfilling || ""}
                            onChange={(e) =>
                              handleTierFieldChange(
                                idx,
                                "cfilling",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select</option>
                            {fillings.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                  {idx < formData.tiers.length - 1 && (
                    <hr className="dotted-line" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* --- COLLAPSIBLE ADDITIONAL SPECIFICATION --- */}
        <div
          className="elc-spec-toggle"
          onClick={() => setAddSpecOpen((o) => !o)}
        >
          <span className="elc-spec-toggle-label">
            Additional Specification
          </span>
          {addSpecOpen ? (
            <FaChevronUp size={10} />
          ) : (
            <FaChevronDown size={10} />
          )}
        </div>

        {addSpecOpen && (
          <div className="elc-spec-body">
            {/* Name on Cake + Position — only shown in read-only mode when data exists */}
            {(!isAddSpecReadOnly || nameCake) && (
              <div className="form-group-row">
                <div className="form-group-item" style={{ flex: 2 }}>
                  {isAddSpecReadOnly ? (
                    <span
                      style={{
                        fontWeight: "normal",
                        fontSize: "11px",
                        color: "#555",
                      }}
                    >
                      {nameCake || ""}
                    </span>
                  ) : (
                    <input
                      type="text"
                      className="elc-text-input"
                      placeholder="Name on Cake"
                      value={nameCake}
                      onChange={(e) => {
                        const v = e.target.value;
                        setNameCake(v);
                        if (!v) setMsgPos("");
                        else if (!msgPos) setMsgPos("Best Fit");
                      }}
                    />
                  )}
                </div>
                <div className="form-group-item">
                  {isAddSpecReadOnly ? (
                    <span
                      style={{
                        fontWeight: "normal",
                        fontSize: "11px",
                        color: "#555",
                      }}
                    >
                      {nameCake ? msgPos || "" : ""}
                    </span>
                  ) : (
                    <select
                      className="elc-select-input"
                      value={msgPos}
                      onChange={(e) => setMsgPos(e.target.value)}
                      disabled={!nameCake}
                    >
                      <option value="" disabled hidden>
                        Position
                      </option>
                      <option value="Best Fit">Best Fit</option>
                      <option value="Top">Top</option>
                      <option value="Bottom">Bottom</option>
                      <option value="Front">Front</option>
                    </select>
                  )}
                </div>
              </div>
            )}

            {(!isAddSpecReadOnly ||
              (nameCake && (dispMsg || specialWishes))) && (
              <hr className="dotted-line" />
            )}

            {/* Message / Special Wishes — only shown in read-only mode when data exists */}
            {(!isAddSpecReadOnly || dispMsg || specialWishes) && (
              <div className="form-group-row">
                <div className="form-group-item" style={{ flex: 1 }}>
                  {isAddSpecReadOnly ? (
                    <span
                      style={{
                        fontWeight: "normal",
                        fontSize: "11px",
                        color: "#555",
                      }}
                    >
                      {dispMsg || specialWishes || ""}
                    </span>
                  ) : (
                    <input
                      type="text"
                      className="elc-text-input"
                      placeholder="Message / Special Wishes"
                      value={dispMsg}
                      onChange={(e) => setDispMsg(e.target.value)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── COLLAPSIBLE DELIVERY ── */}
        <div
          className="elc-spec-toggle"
          onClick={() => setDelivOpen((o) => !o)}
        >
          <span className="elc-spec-toggle-label">Delivery Details</span>
          {delivOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
        </div>

        {delivOpen && (
          <div className="elc-spec-body">
            {/* Mode dropdown – own row */}
            <div style={{ marginBottom: "3px" }}>
              <select
                value={deliveryMode}
                onChange={(e) => {
                  const v = e.target.value;
                  setDeliveryMode(v);
                  if (v === "Delivery") {
                    if (!addressSet) setShowAddAddressForm(true);
                  } else {
                    setSelectedAddress(null);
                    setAddressSet(false);
                    setShowAddAddressForm(false);
                  }
                }}
                disabled={isDeliveryReadOnly}
                className="elc-dt-input"
                style={{ width: "100%" }}
              >
                <option value="Delivery">Delivery</option>
                <option value="PickUp">Pick Up</option>
              </select>
            </div>

            {/* Date + Time in one row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "3px",
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  flex: 1,
                }}
              >
                <input
                  type="date"
                  className="elc-dt-input"
                  value={deliveryDate}
                  min={minDeliveryDate}
                  style={{
                    backgroundColor:
                      !isDeliveryReadOnly && !deliveryDate
                        ? "rgba(250,55,55,0.1)"
                        : undefined,
                    width: "100%",
                  }}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (isDateDisabled(v)) {
                      setDeliveryDate("");
                      setDeliveryTime("");
                      setOffDayWarning(true);
                    } else {
                      setOffDayWarning(false);
                      if (getDateType(v) !== getDateType(deliveryDate))
                        setDeliveryTime("");
                      setDeliveryDate(v);
                    }
                  }}
                  disabled={isDeliveryReadOnly}
                />
              </div>
              <select
                className="elc-dt-input"
                value={deliveryTime}
                style={{
                  flex: 1,
                  backgroundColor:
                    !isDeliveryReadOnly && deliveryDate && !deliveryTime
                      ? "rgba(250,55,55,0.1)"
                      : undefined,
                }}
                onChange={(e) => setDeliveryTime(e.target.value)}
                disabled={isDeliveryReadOnly || !deliveryDate}
              >
                <option value="">Select Time</option>
                {availableTimeSlots.map((s, i) => (
                  <option key={i} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Address section (Delivery only) */}
            {deliveryMode === "Delivery" && showAddAddressForm && (
              <hr className="dotted-line" style={{ margin: "2px 0" }} />
            )}
            {/* Selected address display */}
            {deliveryMode === "Delivery" &&
              addressSet &&
              selectedAddress &&
              !showAddAddressForm && (
                <div
                  className="elc-address-display"
                  style={{ paddingLeft: "4px", marginTop: "3px" }}
                >
                  <span
                    className="elc-address-text"
                    style={{
                      fontWeight: "normal",
                      fontSize: "12px",
                    }}
                  >
                    {selectedAddress}
                  </span>
                  {!isDeliveryReadOnly && (
                    <button
                      type="button"
                      onClick={() => setShowAddAddressForm(true)}
                      className="elc-inline-edit-btn"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "0 3px",
                        flexShrink: 0,
                      }}
                    >
                      <FaEdit size={10} />
                    </button>
                  )}
                </div>
              )}

            {/* Address picker */}
            {deliveryMode === "Delivery" && showAddAddressForm && (
              <div style={{ marginTop: "4px", marginBottom: "2px" }}>
                {addresses.map((addr, i) => {
                  const str = `${addr.value[0]} ${addr.value[1]}, ${addr.value[2]}`;
                  return (
                    <div
                      key={i}
                      className="elc-addr-option"
                      style={{
                        paddingLeft: "18px",
                        paddingTop: i === 0 ? "4px" : "0",
                        paddingBottom: "4px",
                      }}
                    >
                      <label className="elc-radio-label">
                        <input
                          type="radio"
                          name={`elcAddr-${enquiryId}`}
                          value={str}
                          checked={selectedAddress === str}
                          onChange={() => {
                            setSelectedAddress(str);
                            setAddressSet(true);
                            setShowAddAddressForm(false);
                          }}
                          disabled={isDeliveryReadOnly}
                        />
                        <span className="elc-addr-option-text">
                          {str}
                        </span>
                      </label>
                    </div>
                  );
                })}
                {!isDeliveryReadOnly && (
                  <>
                    {addresses.length > 0 && (
                      <hr className="dotted-line" style={{ margin: "4px 0" }} />
                    )}
                    <div
                      className="elc-new-addr-inputs"
                      style={{ flexDirection: "column", gap: "6px" }}
                    >
                      {/* Row 1: Pin / Location + House No. */}
                      <div style={{ display: "flex", gap: "6px" }}>
                        {/* Pincode / Location with autocomplete */}
                        <div style={{ position: "relative", flex: 1 }}>
                          <input
                            type="text"
                            placeholder="Pin / Location"
                            value={newAddress.pinCode}
                            onChange={async (e) => {
                              const val = e.target.value;
                              setNewAddress((p) => ({
                                ...p,
                                pinCode: val,
                                pincodeRaw: "",
                                cityRaw: "",
                              }));
                              if (elcLocDebounceRef.current)
                                clearTimeout(elcLocDebounceRef.current);
                              if (val.length >= 2) {
                                setElcLocLoading(true);
                                elcLocDebounceRef.current = setTimeout(
                                  async () => {
                                    try {
                                      if (
                                        !elcAllLocalitiesRef.current &&
                                        !elcLocalitiesLoadingRef.current
                                      ) {
                                        elcLocalitiesLoadingRef.current = true;
                                        const res = await fetch("/de-plz.json");
                                        elcAllLocalitiesRef.current =
                                          await res.json();
                                        elcLocalitiesLoadingRef.current = false;
                                      }
                                      let attempts = 0;
                                      while (
                                        elcLocalitiesLoadingRef.current &&
                                        attempts++ < 40
                                      ) {
                                        await new Promise((r) =>
                                          setTimeout(r, 100)
                                        );
                                      }
                                      const all =
                                        elcAllLocalitiesRef.current || [];
                                      const isNumeric = /^\d+$/.test(
                                        val.trim()
                                      );
                                      const valLower = val.toLowerCase();
                                      let suggestions;
                                      if (isNumeric) {
                                        suggestions = all
                                          .filter((r) => r[0].startsWith(val))
                                          .slice(0, 8)
                                          .map((r) => ({
                                            postcode: r[0],
                                            city: r[1],
                                            isNumeric: true,
                                          }));
                                      } else {
                                        const seen = new Set();
                                        suggestions = [];
                                        for (const r of all) {
                                          const name = r[1];
                                          if (
                                            name
                                              .toLowerCase()
                                              .startsWith(valLower) &&
                                            !seen.has(name)
                                          ) {
                                            seen.add(name);
                                            suggestions.push({
                                              postcode: r[0],
                                              city: name,
                                              isNumeric: false,
                                            });
                                            if (suggestions.length === 8) break;
                                          }
                                        }
                                        suggestions.sort((a, b) =>
                                          a.city.localeCompare(b.city, "de")
                                        );
                                      }
                                      setElcLocSuggestions(suggestions);
                                    } catch {
                                      setElcLocSuggestions([]);
                                    } finally {
                                      setElcLocLoading(false);
                                    }
                                  },
                                  300
                                );
                              } else {
                                setElcLocSuggestions([]);
                                setElcLocLoading(false);
                              }
                            }}
                            onBlur={() =>
                              setTimeout(() => setElcLocSuggestions([]), 200)
                            }
                            style={{
                              width: "100%",
                              padding: "4px 6px",
                              border: "1px solid #ddd",
                              borderRadius:
                                elcLocSuggestions.length > 0 || elcLocLoading
                                  ? "4px 4px 0 0"
                                  : "4px",
                              fontSize: "12px",
                              boxSizing: "border-box",
                              fontFamily: "inherit",
                            }}
                          />
                          {(elcLocLoading || elcLocSuggestions.length > 0) && (
                            <div
                              className="elc-suggestion-dropdown"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                background: "white",
                                border: "1px solid #ddd",
                                borderTop: "none",
                                borderRadius: "0 0 4px 4px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                zIndex: 50,
                                maxHeight: "160px",
                                overflowY: "auto",
                              }}
                            >
                              {elcLocLoading && (
                                <div
                                  style={{
                                    padding: "6px 10px",
                                    fontSize: "12px",
                                    color: "#999",
                                  }}
                                >
                                  Searching...
                                </div>
                              )}
                              {elcLocSuggestions.map((s, i) => (
                                <div
                                  key={i}
                                  className="elc-suggestion-item"
                                  onMouseDown={(ev) => {
                                    ev.preventDefault();
                                    const label = s.isNumeric
                                      ? s.postcode
                                      : s.city;
                                    setNewAddress((p) => ({
                                      ...p,
                                      pinCode: label,
                                      pincodeRaw: s.isNumeric ? s.postcode : "",
                                      cityRaw: s.city,
                                    }));
                                    setElcLocSuggestions([]);
                                  }}
                                  style={{
                                    padding: "6px 10px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    borderBottom:
                                      i < elcLocSuggestions.length - 1
                                        ? "1px solid #f0f0f0"
                                        : "none",
                                    background: "white",
                                  }}
                                >
                                  {s.isNumeric ? (
                                    <>
                                      <span style={{ fontWeight: 600 }}>
                                        {s.postcode}
                                      </span>
                                      <span
                                        style={{
                                          margin: "0 4px",
                                          color: "#aaa",
                                        }}
                                      >
                                        –
                                      </span>
                                      <span style={{ color: "#555" }}>
                                        {s.city}
                                      </span>
                                    </>
                                  ) : (
                                    <span>{s.city}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* House No. */}
                        <input
                          type="text"
                          placeholder="House No."
                          value={newAddress.houseNumber}
                          onChange={(e) =>
                            setNewAddress((p) => ({
                              ...p,
                              houseNumber: e.target.value,
                            }))
                          }
                          style={{
                            width: "72px",
                            flex: "0 0 72px",
                            padding: "4px 6px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            fontSize: "12px",
                            boxSizing: "border-box",
                            fontFamily: "inherit",
                          }}
                        />
                      </div>
                      {/* end pin+house row */}
                      {/* Street with autocomplete */}
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          placeholder="Street"
                          value={newAddress.streetName}
                          onChange={async (e) => {
                            const val = e.target.value;
                            setNewAddress((p) => ({ ...p, streetName: val }));
                            if (elcStreetDebounceRef.current)
                              clearTimeout(elcStreetDebounceRef.current);
                            if (val.length >= 3) {
                              setElcStreetLoading(true);
                              elcStreetDebounceRef.current = setTimeout(
                                async () => {
                                  try {
                                    const pincodeNum =
                                      newAddress.pincodeRaw ||
                                      (/^\d{5}$/.test(newAddress.pinCode.trim())
                                        ? newAddress.pinCode.trim()
                                        : "");
                                    const cityName =
                                      newAddress.cityRaw ||
                                      (!/^\d/.test(newAddress.pinCode)
                                        ? newAddress.pinCode
                                            .split(" ")
                                            .slice(-1)[0]
                                        : "");
                                    const valLower = val.toLowerCase();
                                    let rows = [];
                                    if (pincodeNum) {
                                      if (
                                        !elcStreetCacheRef.current[pincodeNum]
                                      ) {
                                        const pages = [];
                                        for (let pg = 1; pg <= 10; pg++) {
                                          const d = await fetch(
                                            `https://openplzapi.org/de/Streets?postalCode=${encodeURIComponent(
                                              pincodeNum
                                            )}&pageSize=50&page=${pg}`
                                          )
                                            .then((r) => r.json())
                                            .catch(() => []);
                                          if (
                                            !Array.isArray(d) ||
                                            d.length === 0
                                          )
                                            break;
                                          pages.push(...d);
                                          if (d.length < 50) break;
                                        }
                                        elcStreetCacheRef.current[pincodeNum] =
                                          pages;
                                      }
                                      rows =
                                        elcStreetCacheRef.current[pincodeNum];
                                    } else if (cityName) {
                                      const d = await fetch(
                                        `https://openplzapi.org/de/Streets?name=${encodeURIComponent(
                                          val
                                        )}&locality=${encodeURIComponent(
                                          cityName
                                        )}&pageSize=50`
                                      )
                                        .then((r) => r.json())
                                        .catch(() => []);
                                      rows = Array.isArray(d) ? d : [];
                                      if (
                                        rows.filter((r) =>
                                          (r.name || "")
                                            .toLowerCase()
                                            .startsWith(valLower)
                                        ).length === 0
                                      ) {
                                        const d2 = await fetch(
                                          `https://openplzapi.org/de/Streets?locality=${encodeURIComponent(
                                            cityName
                                          )}&pageSize=50`
                                        )
                                          .then((r) => r.json())
                                          .catch(() => []);
                                        if (Array.isArray(d2) && d2.length > 0)
                                          rows = d2;
                                      }
                                    } else {
                                      const d = await fetch(
                                        `https://openplzapi.org/de/Streets?name=${encodeURIComponent(
                                          val
                                        )}&pageSize=50`
                                      )
                                        .then((r) => r.json())
                                        .catch(() => []);
                                      rows = Array.isArray(d) ? d : [];
                                    }
                                    const filtered = rows.filter((r) =>
                                      (r.name || "")
                                        .toLowerCase()
                                        .startsWith(valLower)
                                    );
                                    const nameCount = {};
                                    filtered.forEach((r) => {
                                      nameCount[r.name] =
                                        (nameCount[r.name] || 0) + 1;
                                    });
                                    const seen = new Set();
                                    const suggestions = [];
                                    for (const r of filtered) {
                                      const key = `${r.name}|${r.postalCode}`;
                                      if (seen.has(key)) continue;
                                      seen.add(key);
                                      suggestions.push({
                                        name: r.name,
                                        postcode: r.postalCode || "",
                                        locality: r.locality || "",
                                        showLabel: nameCount[r.name] > 1,
                                      });
                                      if (suggestions.length >= 10) break;
                                    }
                                    suggestions.sort((a, b) =>
                                      a.name.localeCompare(b.name, "de")
                                    );
                                    setElcStreetSuggestions(suggestions);
                                  } catch {
                                    setElcStreetSuggestions([]);
                                  } finally {
                                    setElcStreetLoading(false);
                                  }
                                },
                                400
                              );
                            } else {
                              setElcStreetSuggestions([]);
                              setElcStreetLoading(false);
                            }
                          }}
                          onBlur={() =>
                            setTimeout(() => setElcStreetSuggestions([]), 200)
                          }
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            border: "1px solid #ddd",
                            borderRadius:
                              elcStreetSuggestions.length > 0 ||
                              elcStreetLoading
                                ? "4px 4px 0 0"
                                : "4px",
                            fontSize: "12px",
                            boxSizing: "border-box",
                            fontFamily: "inherit",
                          }}
                        />
                        {(elcStreetLoading ||
                          elcStreetSuggestions.length > 0) && (
                          <div
                            className="elc-suggestion-dropdown"
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              background: "white",
                              border: "1px solid #ddd",
                              borderTop: "none",
                              borderRadius: "0 0 4px 4px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                              zIndex: 50,
                              maxHeight: "160px",
                              overflowY: "auto",
                            }}
                          >
                            {elcStreetLoading && (
                              <div
                                style={{
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                  color: "#999",
                                }}
                              >
                                Searching...
                              </div>
                            )}
                            {elcStreetSuggestions.map((s, i) => (
                              <div
                                key={i}
                                className="elc-suggestion-item"
                                onMouseDown={(ev) => {
                                  ev.preventDefault();
                                  setNewAddress((p) => {
                                    const currentPin = (p.pinCode || "").trim();
                                    const isPincode = /^\d{5}$/.test(
                                      currentPin
                                    );
                                    const updates = {
                                      ...p,
                                      streetName: s.name,
                                    };
                                    if (!isPincode && s.postcode) {
                                      // searched by city ? replace with pincode
                                      updates.pinCode = s.postcode;
                                    }
                                    return updates;
                                  });
                                  setElcStreetSuggestions([]);
                                }}
                                style={{
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                  cursor: "pointer",
                                  borderBottom:
                                    i < elcStreetSuggestions.length - 1
                                      ? "1px solid #f0f0f0"
                                      : "none",
                                  background: "white",
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span>{s.name}</span>
                                {s.showLabel && s.postcode && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      color: "#999",
                                      marginLeft: "6px",
                                    }}
                                  >
                                    {s.postcode}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* end elc-new-addr-inputs */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginTop: "4px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          fontSize: "11px",
                          color: "#555",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={addAsHomeAddress}
                          onChange={(e) =>
                            setAddAsHomeAddress(e.target.checked)
                          }
                          style={{
                            width: "13px",
                            height: "13px",
                            margin: 0,
                            flexShrink: 0,
                          }}
                        />
                        Add as Home Address
                      </label>
                      <button
                        type="button"
                        onClick={handleAddAddress}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#666",
                          cursor: "pointer",
                          fontSize: "14px",
                          lineHeight: 1,
                          marginLeft: "auto",
                        }}
                      >
                        &#10003;
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddAddressForm(false);
                          setAddAsHomeAddress(false);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#666",
                          cursor: "pointer",
                        }}
                      >
                        <FaTimes size={13} />
                      </button>
                    </div>
                  </>
                )}
                {addresses.length === 0 && isDeliveryReadOnly && (
                  <span style={{ fontSize: "11px", color: "#999" }}>
                    No address saved
                  </span>
                )}
              </div>
            )}
            {deliveryMode === "Delivery" &&
              !addressSet &&
              !showAddAddressForm &&
              !isDeliveryReadOnly && (
                <button
                  type="button"
                  onClick={() => setShowAddAddressForm(true)}
                  className="elc-add-address-btn"
                  style={{ color: "#fa4848", padding: "3px 8px" }}
                >
                  + Add Address
                </button>
              )}
          </div>
        )}

        {/* --- COLLAPSIBLE CONTACT DETAILS --- */}
        <div
          className="elc-spec-toggle"
          onClick={() => setContactOpen((o) => !o)}
        >
          <span className="elc-spec-toggle-label">Contact Details</span>
          {contactOpen ? (
            <FaChevronUp size={10} />
          ) : (
            <FaChevronDown size={10} />
          )}
        </div>

        {contactOpen && (
          <div className="elc-spec-body">
            {/* View mode — phone set */}
            {!showAddPhoneForm && (profilePhone || phoneVerified) && (
              <div className="elc-address-display">
                <span
                  className="elc-address-text"
                  style={{
                    fontWeight: "normal",
                    fontSize: "12px",
                    color: "#333",
                  }}
                >
                  {profilePhone}
                </span>
                {!isDeliveryReadOnly && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhoneInput(stripAnyISD(profilePhone));
                      setShowAddPhoneForm(true);
                      setPhoneError("");
                      setPhoneVerified(false);
                      setPhoneCodeSent(false);
                      setPhoneCodeInput("");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "0 3px",
                      flexShrink: 0,
                    }}
                  >
                    <FaEdit size={10} />
                  </button>
                )}
              </div>
            )}

            {/* View mode € no phone, read-only */}
            {!showAddPhoneForm &&
              !profilePhone &&
              !phoneVerified &&
              isDeliveryReadOnly && (
                <span style={{ fontSize: "11px", color: "#999" }}>
                  No phone saved
                </span>
              )}

            {/* View mode € no phone, editable */}
            {!showAddPhoneForm &&
              !profilePhone &&
              !phoneVerified &&
              !isDeliveryReadOnly && (
                <button
                  type="button"
                  onClick={() => {
                    setPhoneInput("");
                    setShowAddPhoneForm(true);
                    setPhoneError("");
                    setPhoneVerified(false);
                    setPhoneCodeSent(false);
                    setPhoneCodeInput("");
                  }}
                  className="elc-add-address-btn"
                  style={{ color: "#fa4848", padding: "3px 8px" }}
                >
                  + Add Phone Number
                </button>
              )}

            {/* Edit mode */}
            {showAddPhoneForm && !isDeliveryReadOnly && (
              <div style={{ marginTop: "4px" }}>
                {/* ISD prefix + input + Ask Code button inline */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    marginBottom: "3px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#555",
                      border: "1px solid #ddd",
                      borderRadius: "4px 0 0 4px",
                      padding: "4px 6px",
                      background: "#f5f5f5",
                      borderRight: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {isdCode}
                  </span>
                  <input
                    type="tel"
                    placeholder="Phone number"
                    value={phoneInput}
                    onChange={(e) => {
                      const v = e.target.value
                        .replace(/\D/g, "")
                        .replace(/^0+/, "")
                        .slice(0, 11);
                      setPhoneInput(v);
                      setPhoneError("");
                      setPhoneVerified(false);
                      setPhoneCodeSent(false);
                      setPhoneCodeInput("");
                    }}
                    style={{
                      flex: 1,
                      fontSize: "11px",
                      padding: "4px 6px",
                      border: "1px solid #ddd",
                      borderRadius: "0 4px 4px 0",
                      boxSizing: "border-box",
                      minWidth: 0,
                    }}
                  />
                  {/* Ask Code — when number changed and not yet sent */}
                  {phoneChanged && !phoneCodeSent && (
                    <button
                      type="button"
                      onClick={handleSendPhoneCode}
                      disabled={phoneSendingCode || phoneInput.length < 5}
                      style={{
                        fontSize: "10px",
                        padding: "3px 6px",
                        background:
                          phoneSendingCode || phoneInput.length < 5
                            ? "#e0e0e0"
                            : "#fa4848",
                        color:
                          phoneSendingCode || phoneInput.length < 5
                            ? "#aaa"
                            : "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor:
                          phoneSendingCode || phoneInput.length < 5
                            ? "default"
                            : "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {phoneSendingCode ? "Sending..." : "Verify"}
                    </button>
                  )}
                  {/* Resend — once code already sent, not yet verified */}
                  {phoneChanged && phoneCodeSent && !phoneVerified && (
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneCodeSent(false);
                        handleSendPhoneCode();
                      }}
                      disabled={
                        phoneSendingCode ||
                        phoneResendCountdown > 0 ||
                        phoneInput.length < 5
                      }
                      style={{
                        fontSize: "10px",
                        padding: "3px 6px",
                        background:
                          phoneSendingCode ||
                          phoneResendCountdown > 0 ||
                          phoneInput.length < 5
                            ? "#e0e0e0"
                            : "#fa4848",
                        color:
                          phoneSendingCode ||
                          phoneResendCountdown > 0 ||
                          phoneInput.length < 5
                            ? "#aaa"
                            : "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor:
                          phoneSendingCode ||
                          phoneResendCountdown > 0 ||
                          phoneInput.length < 5
                            ? "default"
                            : "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {phoneSendingCode
                        ? "Sending..."
                        : phoneResendCountdown > 0
                        ? `Resend (${phoneResendCountdown}s)`
                        : "Resend Code"}
                    </button>
                  )}
                </div>

                {/* 6-digit code — visible only after Ask Code clicked, hidden after verified */}
                {phoneChanged && phoneCodeSent && !phoneVerified && (
                  <div style={{ marginBottom: "5px" }}>
                    <input
                      type="text"
                      placeholder="6-digit code"
                      value={phoneCodeInput}
                      disabled={phoneVerifying}
                      onChange={(e) => {
                        const v = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6);
                        setPhoneCodeInput(v);
                        setPhoneError("");
                        if (v.length === 6) handleVerifyPhoneCode(v);
                      }}
                      style={{
                        width: "90px",
                        fontSize: "12px",
                        padding: "4px 5px",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        letterSpacing: "3px",
                        textAlign: "center",
                        color: "#333",
                        background: "#fff",
                        display: "block",
                        marginBottom: "3px",
                      }}
                    />
                    {phoneError === "invalid" && (
                      <span style={{ fontSize: "10px", color: "#fa4848" }}>
                        Security code is invalid
                      </span>
                    )}
                    {phoneError && phoneError !== "invalid" && (
                      <span style={{ fontSize: "10px", color: "#27ae60" }}>
                        {phoneError}
                      </span>
                    )}
                    {phoneVerifying && (
                      <span style={{ fontSize: "10px", color: "#888" }}>
                        Verification in progress...
                      </span>
                    )}
                  </div>
                )}
                {phoneVerified && (
                  <span style={{ fontSize: "10px", color: "#27ae60" }}>
                    &#10003; Verified
                  </span>
                )}

                {/* Accept + Cancel */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "4px",
                  }}
                >
                  <button
                    type="button"
                    onClick={handleConfirmPhone}
                    disabled={phoneChanged && !phoneVerified}
                    className="elc-inline-edit-btn"
                    style={{
                      background: "none",
                      border: "none",
                      color: phoneChanged && !phoneVerified ? "#ccc" : "#666",
                      cursor: phoneChanged && !phoneVerified ? "default" : "pointer",
                      fontSize: phoneVerified ? "18px" : "14px",
                      fontWeight: phoneVerified ? "700" : "400",
                      lineHeight: 1,
                      marginLeft: "auto",
                    }}
                  >
                    &#10003;
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddPhoneForm(false);
                      setPhoneInput("");
                      setPhoneCodeSent(false);
                      setPhoneCodeInput("");
                      setPhoneError("");
                      setPhoneVerified(false);
                      setAddAsPrimaryPhone(false);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#666",
                      cursor: "pointer",
                    }}
                    className="elc-inline-edit-btn"
                  >
                    <FaTimes size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── QUANTITY & COST ── */}
        <hr className="dotted-line" />
        <div className="elc-qty-section">
          <div className="elc-qty-cost-row">
            <div className="form-group-item">
              <label>Quantity:</label>
              <div className="field-value">
                <span>{formData.quantity ? `${formData.quantity}kg` : ""}</span>
              </div>
            </div>
            {(isSuper && !isCakeReadOnly) ||
            hasCosts ||
            costest ||
            profile?.ccost ? (
              <div className="form-group-item">
                <label>
                  {isUpToReviewPending
                    ? "Cost Estimate:"
                    : hasCosts
                    ? "Cost:"
                    : "Cost Est.:"}
                </label>
                <div className="field-value">
                  {isSuper && !isCakeReadOnly ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                    >
                      <span className="elc-cost-euro">{getCurrencySymbol()}</span>
                      <input
                        type="text"
                        placeholder="0.00"
                        className="elc-cost-input"
                        value={
                          imageCosts[imgIndex] ||
                          (imgIndex === 0 ? costest || "" : "") ||
                          ""
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          setImageCosts((prev) =>
                            prev.map((c, i) => (i === imgIndex ? v : c))
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <span>
                      {(() => {
                        if (hasCosts) {
                          const imgCost =
                            parseFloat(
                              String(imageCosts[imgIndex] || "").replace(
                                /EUR|€/g,
                                ""
                              )
                            ) || 0;
                          const delNum = delCost
                            ? parseFloat(String(delCost).replace(/EUR|€/g, "")) ||
                              0
                            : 0;
                          const total = imgCost + delNum;
                          if (total <= 0) return "";
                          const incDel =
                            isNormalUser && delNum > 0 ? " (Inc. Delivery)" : "";
                          return `${getCurrencySymbol()}${total}${incDel}`;
                        }
                        const v = costest || profile?.ccost;
                        if (!v || v === "N/A") return "";
                        return `${getCurrencySymbol()}${String(v).replace(/^EUR|^€/, "")}`;
                      })()}
                    </span>
                  )}
                </div>
              </div>
            ) : null}
          </div>
          {!isNormalUser &&
            deliveryMode === "Delivery" &&
            reqState !== "Request Review" && (
                <div className="form-group-item">
                  <label>Del Cost:</label>
                  <div className="field-value" style={{ position: "relative" }}>
                    {isSuper && !isCakeReadOnly ? (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                          }}
                        >
                          <span className="elc-cost-euro">
                            {getCurrencySymbol()}
                          </span>
                          <input
                            type="text"
                            placeholder="0.00"
                            className="elc-cost-input"
                            value={delCost}
                            onChange={(e) => {
                              setDelCost(e.target.value);
                            }}
                          />
                        </div>
                        {delCostLoading && (
                          <span
                            style={{
                              fontSize: "10px",
                              color: "#999",
                              marginLeft: "4px",
                            }}
                          >
                            calculating{getCurrencySymbol()}
                          </span>
                        )}
                      </>
                    ) : (
                      <span>
                        {delCost
                          ? `${getCurrencySymbol()}${String(delCost).replace(/^EUR|^€/, "")}`
                          : ""}
                      </span>
                    )}
                  </div>
                  {autoCalcKm != null && (
                    <span style={{ fontSize: "11px", color: "#888", marginLeft: "6px" }}>
                      ({autoCalcKm} km)
                    </span>
                  )}
                </div>
              )}

        </div>

        {/* ── COMMENTS + CHANGELOG ── */}
        {(isSuper || reviewFeedback.trim() !== "") && (
          <div className="elc-bottom-section">
            <div className="elc-feedback">
              <label className="elc-feedback-label">Change Comments:</label>
              {isSuper ? (
                <textarea
                  className="elc-feedback-input"
                  rows={3}
                  value={reviewFeedback}
                  onChange={(e) => setReviewFeedback(e.target.value)}
                />
              ) : (
                <pre className="elc-feedback-input elc-feedback-readonly">
                  {renderFeedback(reviewFeedback)}
                </pre>
              )}
              {changeLog.length > 0 && (
                <div className="elc-changelog">
                  <span className="elc-changelog-header">
                    Pending Changes:{" "}
                  </span>
                  {changeLog.map((c, i) => (
                    <span key={i} className="elc-changelog-entry">
                      {i > 0 && " | "}
                      {c.label}: <s className="elc-changelog-old">{c.old}</s>{" "}
                      <span className="elc-changelog-new">{c.new}</span>
                    </span>
                  ))}
                  <span className="elc-changelog-note">
                    {" "}
                    (appended to Comments on Save)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {offDayWarning && (
          <div
            style={{
              padding: "4px 0 2px 0",
              fontSize: "12px",
              color: "#fa4848",
              textAlign: "center",
            }}
          >
            Off Day, please select a different date.
          </div>
        )}

        {/* ── ACTION BUTTON ── */}
        {((!isOtimelineLoaded || !isProcessControlsLoaded) ||
          (isOtimelineLoaded &&
            isProcessControlsLoaded &&
            (canEnableButton() || showChangeButtons || showOptionDropdown))) && (
          <>
            <hr className="dotted-line" />
            <div className="elc-action-section-bottom">
              {(!isOtimelineLoaded || !isProcessControlsLoaded) && (
                <button className="elc-action-btn" disabled>
                  <span className="elc-btn-spinner" />
                </button>
              )}
              {isOtimelineLoaded &&
                isProcessControlsLoaded &&
                (canEnableButton() || showChangeButtons || showOptionDropdown) && (
                  <>
                    {showChangeButtons ? (
                      <div className="elc-action-buttons">
                        <button className="elc-action-btn" onClick={handleAccept}>
                          Accept
                        </button>
                        <button
                          className="elc-action-btn"
                          onClick={handleRejectClick}
                        >
                          Reject
                        </button>
                      </div>
                    ) : showOptionDropdown && optionAction ? (
                      <div className="elc-action-buttons">
                        <button
                          className="elc-action-btn"
                          onClick={handleRequestReview}
                          disabled={!isEnquiryButtonEnabled}
                          title={
                            !isEnquiryButtonEnabled
                              ? saving || hasChanges
                                ? "Saving..."
                                : !isPhoneProvided
                                ? "Please add & verify your phone number"
                                : deliveryDetailsValid
                                ? "This action is restricted"
                                : "Please fill up delivery details"
                              : ""
                          }
                        >
                          {saving ? <span className="elc-btn-spinner" /> : buttonText}
                        </button>
                        <select
                          className="elc-option-dropdown"
                          onChange={handleOptionSelect}
                          value=""
                        >
                          <option value="" disabled>
                            Select an option
                          </option>
                          <option value={optionAction.showState1}>
                            {optionAction.showState1}
                          </option>
                          <option value={optionAction.showState2}>
                            {optionAction.showState2}
                          </option>
                        </select>
                      </div>
                    ) : (
                      <button
                        className={`elc-action-btn${isEnquiryButtonEnabled ? ' elc-action-btn--pulse' : ''}`}
                        onClick={handleRequestReview}
                        disabled={!isEnquiryButtonEnabled}
                        title={
                          !isEnquiryButtonEnabled
                            ? saving || hasChanges
                              ? "Saving..."
                              : !isPhoneProvided
                              ? "Please add & verify your phone number"
                              : deliveryDetailsValid
                              ? "This action is restricted"
                              : "Please fill up delivery details"
                            : ""
                        }
                      >
                        {saving ? <span className="elc-btn-spinner" /> : buttonText}
                      </button>
                    )}
                  </>
                )}
            </div>
          </>
        )}
      </div>

      {/* ── POPUPS ── */}
      {showPopup && (
        <div className="elc-popup-overlay">
          <div className="elc-popup-content">
            <button
              className="elc-popup-close"
              onClick={() => setShowPopup(false)}
            >
              ×
            </button>
            <p>
              {deliveryDetailsValid
                ? "This action is restricted"
                : "Please fill up delivery details"}
            </p>
          </div>
        </div>
      )}
      {showOngoingEnquiryModal && (
        <div className="elc-popup-overlay">
          <div className="elc-reject-popup">
            <div
              className="elc-reject-header"
              style={{ background: "#fa4848", color: "#fff" }}
            >
              <span>On-going Enquiry not Finished</span>
              <button
                className="elc-popup-close"
                onClick={() => setShowOngoingEnquiryModal(false)}
              >
                €
              </button>
            </div>
            <div className="elc-reject-body">
              <p style={{ fontSize: "13px" }}>
                You have an active enquiry in progress. Please wait for it to be
                completed before sending a new request.
              </p>
            </div>
            <div className="elc-reject-footer">
              <button
                className="elc-action-btn"
                style={{ background: "#fa4848" }}
                onClick={() => setShowOngoingEnquiryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showCostMissingPopup && (
        <div className="elc-popup-overlay">
          <div className="elc-reject-popup">
            <div
              className="elc-reject-header"
              style={{ background: "#fa4848", color: "#fff" }}
            >
              <span>Cost Required</span>
              <button
                className="elc-popup-close"
                onClick={() => setShowCostMissingPopup(false)}
              >
                €
              </button>
            </div>
            <div className="elc-reject-body">
              <p style={{ fontSize: "13px" }}>
                Please set cost for all design.
              </p>
            </div>
            <div className="elc-reject-footer">
              <button
                className="elc-action-btn"
                style={{ background: "#fa4848" }}
                onClick={() => setShowCostMissingPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showDelCostMissingPopup && (
        <div className="elc-popup-overlay">
          <div className="elc-reject-popup">
            <div className="elc-reject-header">
              <span>Delivery Cost Required</span>
              <button
                className="elc-popup-close"
                onClick={() => setShowDelCostMissingPopup(false)}
              >
                €
              </button>
            </div>
            <div className="elc-reject-body">
              <p style={{ fontSize: "13px" }}>Please add Delivery Cost.</p>
            </div>
            <div className="elc-reject-footer">
              <button
                className="elc-action-btn"
                style={{ background: "#fa4848" }}
                onClick={() => setShowDelCostMissingPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showImageCheckPopup && (
        <div className="elc-popup-overlay">
          <div className="elc-reject-popup">
            <div
              className="elc-reject-header"
              style={{ background: "#fa4848", color: "#fff" }}
            >
              <span>Design Check</span>
            </div>
            <div className="elc-reject-body">
              <p style={{ fontSize: "13px" }}>
                Please keep only one design before accepting.
              </p>
            </div>
            <div className="elc-reject-footer">
              <button
                className="elc-action-btn"
                style={{ background: "#fa4848" }}
                onClick={() => setShowImageCheckPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showRejectPopup && (
        <div className="elc-popup-overlay">
          <div className="elc-reject-popup">
            <div
              className="elc-reject-header"
              style={{
                background: "#fa4848",
                color: "#fff",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="elc-popup-close"
                style={{ position: "static", color: "#fff" }}
                onClick={() => setShowRejectPopup(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="elc-reject-body">
              <p>Request will be Cancelled</p>
            </div>
            <div className="elc-reject-footer">
              <button className="elc-action-btn" onClick={handleRejectConfirm}>
                Ok
              </button>
            </div>
          </div>
        </div>
      )}
      {showAcceptConfirmModal && (
        <div
          className="elc-popup-overlay"
          onClick={() => {
            setShowAcceptConfirmModal(false);
            setAcceptCheck1(false);
            setAcceptCheck2(false);
            setAcceptCheck3(false);
          }}
        >
          <div
            className="elc-reject-popup"
            style={{ maxWidth: "320px", width: "92%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="elc-reject-header"
              style={{
                background: "#fa4848",
                color: "#fff",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="elc-popup-close"
                style={{ position: "static", color: "#fff" }}
                onClick={() => {
                  setShowAcceptConfirmModal(false);
                  setAcceptCheck1(false);
                  setAcceptCheck2(false);
                  setAcceptCheck3(false);
                }}
              >
                <FaTimes />
              </button>
            </div>
            <div
              className="elc-reject-body"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                textAlign: "left",
              }}
            >
              {[
                [
                  acceptCheck1,
                  setAcceptCheck1,
                  "Reachable through the provided contact information with taking ownership if unreachable.",
                ],
                [
                  acceptCheck2,
                  setAcceptCheck2,
                  "The delivery is limited to the nearest drive-way with temporary parking possibility.",
                ],
                [
                  acceptCheck3,
                  setAcceptCheck3,
                  "Donate for material costs in advance (if necessary) and other costs on delivery by agreed method.",
                ],
              ].map(([checked, setter, label], idx) => (
                <label
                  key={idx}
                  className="elc-accept-check-label"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setter(e.target.checked)}
                    className="elc-accept-checkbox"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <div className="elc-reject-footer">
              <button
                className="elc-action-btn"
                disabled={!(acceptCheck1 && acceptCheck2 && acceptCheck3)}
                style={{
                  opacity:
                    acceptCheck1 && acceptCheck2 && acceptCheck3 ? 1 : 0.4,
                  cursor:
                    acceptCheck1 && acceptCheck2 && acceptCheck3
                      ? "pointer"
                      : "not-allowed",
                }}
                onClick={handleAcceptConfirm}
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      )}
      {showCommentRequiredPopup && (
        <div className="elc-popup-overlay">
          <div className="elc-reject-popup">
            <div className="elc-reject-header">
              <span>Comment Required</span>
              <button
                className="elc-popup-close"
                style={{ position: "static" }}
                onClick={() => {
                  setShowCommentRequiredPopup(false);
                  pendingStateChangeFnRef.current = null;
                }}
              >
                <FaTimes />
              </button>
            </div>
            <div className="elc-reject-body">
              <p style={{ fontSize: "13px", marginBottom: "8px" }}>
                {pendingStateLabel === "Order Cancelled"
                  ? "Please provide cancellation reason."
                  : "Please provide on-hold reason."}
              </p>
              <textarea
                className="elc-feedback-input"
                rows={3}
                placeholder="Enter your comment€"
                value={requiredCommentText}
                onChange={(e) => setRequiredCommentText(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <div className="elc-reject-footer">
              <button
                className="elc-action-btn"
                style={{ background: "#fa4848" }}
                onClick={handleCommentRequiredSubmit}
                disabled={!requiredCommentText.trim()}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      {showFeedbackPopup && (
        <div className="elc-popup-overlay">
          <div className="elc-feedback-popup">
            <div className="elc-feedback-popup-header">
              Provide Your Feedback
            </div>
            <div>
              <label
                className="elc-feedback-label-text"
              >
                Overall Satisfaction
              </label>
              <div className="elc-star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar
                    key={star}
                    className={`elc-star ${
                      feedbackRating >= star ? "filled" : ""
                    }`}
                    onClick={() => setFeedbackRating(star)}
                  />
                ))}
              </div>
            </div>
            <div>
              <label
                className="elc-feedback-label-text"
              >
                Describe your experience{" "}
                {feedbackRating < 4 && feedbackRating > 0
                  ? "(required)"
                  : "(optional)"}
              </label>
              <textarea
                className="elc-feedback-comment"
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Enter your feedback here..."
              />
            </div>
            <div className="elc-feedback-popup-btns">
              <button
                className="elc-action-btn"
                onClick={() => {
                  setShowFeedbackPopup(false);
                  setFeedbackRating(0);
                  setFeedbackComment("");
                }}
              >
                Cancel
              </button>
              <button className="elc-action-btn" onClick={handleFeedbackSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(EnquiryLargeCard);
